import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ethers } from "ethers";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import "./App.css";
import WalletConnection from "./components/WalletConnection";
import WalletSelectionModal from "./components/WalletSelectionModal";
import PaymentRequestModal from "./components/PaymentRequestModal";
import BalancesModal from "./components/BalancesModal";
import Dashboard from "./components/Dashboard";
import TabbedOperations from "./components/TabbedOperations";
import InviteGate from "./components/InviteGate";
import FeedbackModal from "./components/FeedbackModal";
import { AuthService } from "./services/authService";
import { hasUserSiteAccessRecord } from "./services/userSiteAccessService";
import { secureKeyCache } from "./services/secureKeyCache.js";
import { sanitizeNotificationMessage, withCleanError } from "./utils/errorParser.js";
import {
  connectWithWalletConnect,
  disconnectWalletConnect,
  getWalletConnectProvider,
  connectWithTrustWallet,
  connectWithOKXWallet,
  connectWithCoinbaseWallet,
  connectWithRainbowWallet,
  getAvailableWallets,
  discoverEip6963Providers,
  verifyInjectedWalletIdentity,
  getCoinbaseWalletProvider,
  getRainbowWalletProvider,
} from "./services/walletService";
// Route wallet reads/estimateGas through a reliable public RPC (Alchemy) instead
// of Privy's rate-limited rpc, signing stays with the wallet.
import { makeHybridEip1193, retargetHybridChain } from "./utils/hybridProvider";
import { getSavedChainId, setSavedChainId } from "./utils/chainPreference";
import { parseTransferRequestFromUrl, clearTransferRequestFromUrl } from "./utils/transferRequestLink";
import { useBalances } from "./hooks/useBalances";
// Background image is now handled by CSS

// Confidential crypto primitives (provided by the @fairblock/stabletrust SDK)
import init, {
  encrypt_amount,
  decrypt_ciphertext,
  generate_transfer_proof,
  generate_withdraw_proof,
} from "./crypto/confidentialCrypto.js";

/** Set to true to re-enable the invite-only gate (InviteGate component). */
const INVITE_GATE_ENABLED = false;

// Helpers (top-level to reduce nesting inside component)
const formatAddress = (address) =>
  `${address.slice(0, 6)}...${address.slice(-4)}`;

/**
 * Gas-state tint, values straight from the design reference
 * (usernames-desktop.dc.html / usernames-mobile.dc.html renderVals()).
 * 'ok' isn't in here — it renders as the neutral untinted chip/panel.
 */
const GAS_TONE = {
  empty: { ink: "#C4462E", line: "#E4B9AE", fill: "#FDF8F7" },
  low: { ink: "#B08A2E", line: "#E4D2A8", fill: "#FDFBF5" },
};

/** Header gas chip: style + label for a given gasState ('ok'|'low'|'empty'|null). */
const getGasChipVisuals = (gasState, nativeBalance, nativeSymbol) => {
  const isNeutral = !gasState || gasState === "ok"; // null = still loading — read as funded, not alarming
  const tone = !isNeutral ? GAS_TONE[gasState] : null;
  return {
    border: isNeutral ? "#E6E6E1" : tone.line,
    background: isNeutral ? "#FFFFFF" : tone.fill,
    ink: isNeutral ? "#4A4A46" : tone.ink,
    dot: isNeutral ? "#3E8C68" : tone.ink,
    // Always the real figure, even at zero — "No gas" read as more alarming
    // than useful; the tint + dot already carry the severity.
    label: nativeBalance != null ? `${nativeBalance.toFixed(4)} ${nativeSymbol}` : "…",
  };
};

// Safer notification IDs with counter to avoid collisions
let __nid = 0;
// Tracks "type:message" for non-persistent notifications currently on
// screen. Repeating a guard-clause action (e.g. clicking a percentage
// button several times while balance is still null) calls showNotification
// once per click; without this, each call queues its own setNotifications
// updater and duplicates pile up regardless of how those updaters get
// batched, since every one of them is comparing against state that hasn't
// committed yet. A plain module-level Set sidesteps that entirely by
// deduping before React is touched at all. Persistent notifications are
// excluded — they're dismissed by the user via their own close button
// (a direct setNotifications call elsewhere), so this Set has no hook to
// release their key on dismissal.
const __activeNotificationKeys = new Set();
function pushNotification(
  setNotifications,
  message,
  type = "info",
  persistent = false,
) {
  // Strip ethers/RPC dumps from error/warning toasts so no operation can leak
  // technical details even if a caller forgets getCleanErrorMessage.
  const safeMessage = sanitizeNotificationMessage(message, type);
  const key =
    typeof safeMessage === "string"
      ? `${type}:${safeMessage}`
      : `${type}:${Date.now()}-${++__nid}`;
  if (!persistent) {
    if (__activeNotificationKeys.has(key)) {
      return;
    }
    __activeNotificationKeys.add(key);
  }
  const id = `${Date.now()}-${++__nid}`;
  setNotifications((prev) => [
    ...prev,
    { id, message: safeMessage, type, persistent },
  ]);
  // Only auto-dismiss if not persistent
  if (!persistent) {
    // Error messages stay a bit longer to give users time to read them —
    // trimmed down from 15s/10s, which read as far too long once notifications
    // were actually dismissible (the close button used to be broken).
    const timeout = type === "error" ? 6000 : 4000;
    setTimeout(() => {
      __activeNotificationKeys.delete(key);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, timeout);
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Import chain configurations
import { CHAINS, resolveChainEntry, toConfidentialSymbol } from "./config/chains";
import {
  CONTRACT_ABI,
  PENDING_KIND_APPLY,
  PENDING_KIND_DEPOSIT,
  isPendingKind,
  parseAccountCore,
  parsePendingKind,
} from "./config/contractAbi";

// Network Configurations for MetaMask
const NETWORK_CONFIGS = {
  arbitrum: {
    chainId: "0x66eee", // 421614 in hex
    chainName: "Arbitrum Sepolia",
    rpcUrls: ["https://sepolia-rollup.arbitrum.io/rpc"],
    blockExplorerUrls: ["https://sepolia.arbiscan.io"],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  ethereum: {
    chainId: "0xaa36a7", // 11155111 in hex
    chainName: "Ethereum Sepolia",
    rpcUrls: ["https://ethereum-sepolia-rpc.publicnode.com"],
    blockExplorerUrls: ["https://sepolia.etherscan.io"],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  arc: {
    chainId: process.env.REACT_APP_ARC_CHAIN_ID || "0x4cef52", // 5042002 in hex
    chainName: "Arc",
    rpcUrls: [
      process.env.REACT_APP_ARC_RPC_URL || "https://rpc.testnet.arc.network",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_ARC_BLOCK_EXPLORER || "https://testnet.arcscan.app",
    ],
    nativeCurrency: {
      name: "USDC",
      symbol: "USDC",
      decimals: 6,
    },
  },
  base: {
    chainId: "0x14a34", // 84532 in hex (Base Sepolia)
    chainName: "Base Sepolia",
    rpcUrls: [process.env.REACT_APP_BASE_RPC_URL || "https://sepolia.base.org"],
    blockExplorerUrls: [
      process.env.REACT_APP_BASE_BLOCK_EXPLORER ||
        "https://sepolia-explorer.base.org",
    ],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  stable: {
    chainId: process.env.REACT_APP_STABLE_CHAIN_ID || "0x899", // 2201 in hex
    chainName: "Stable Testnet",
    rpcUrls: [
      process.env.REACT_APP_STABLE_RPC_URL || "https://rpc.testnet.stable.xyz",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_STABLE_BLOCK_EXPLORER ||
        "https://explorer.stable.network",
    ],
    nativeCurrency: {
      name: "Stable",
      symbol: "STABLE",
      decimals: 18,
    },
  },
  // Distinct from `bnb_mainnet` (BSC) — never interchange them.
  bnb: {
    chainId: "0x61", // 97 in hex (BNB Smart Chain Testnet)
    chainName: "BNB Smart Chain Testnet",
    rpcUrls: [
      process.env.REACT_APP_BNB_RPC_URL ||
        "https://data-seed-prebsc-1-s1.binance.org:8545",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_BNB_BLOCK_EXPLORER || "https://testnet.bscscan.com",
    ],
    nativeCurrency: {
      name: "BNB",
      symbol: "tBNB",
      decimals: 18,
    },
  },
  // Distinct from `bnb` (BSC Testnet) — never interchange them.
  bnb_mainnet: {
    chainId: "0x38", // 56 in hex (BNB Smart Chain Mainnet)
    chainName: "BNB Smart Chain",
    rpcUrls: [
      process.env.REACT_APP_BNB_MAINNET_RPC_URL ||
        "https://bsc-rpc.publicnode.com",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_BNB_MAINNET_BLOCK_EXPLORER || "https://bscscan.com",
    ],
    nativeCurrency: {
      name: "BNB",
      symbol: "BNB",
      decimals: 18,
    },
  },
  tempo: {
    chainId: process.env.REACT_APP_TEMPO_CHAIN_ID || "0xa5bf", //  in hex
    chainName: "Tempo Testnet",
    rpcUrls: [process.env.REACT_APP_TEMPO_RPC_URL || "https://rpc.tempo.xyz"],
    blockExplorerUrls: [
      process.env.REACT_APP_TEMPO_BLOCK_EXPLORER || "https://explore.tempo.xyz",
    ],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  // Mirrors chains.js's arbitrum_mainnet entry — this app-level wallet-
  // connect config needed its own copy since it's a separate object, not
  // derived from CHAINS. Contract address lives in CHAINS (aligned with
  // @fairblock/stabletrust 2.0.16+). Kept as a distinct key from `arbitrum`
  // (Sepolia) — never interchange them.
  arbitrum_mainnet: {
    chainId: "0xa4b1", // 42161 in hex (Arbitrum One)
    chainName: "Arbitrum",
    rpcUrls: [
      process.env.REACT_APP_ARBITRUM_MAINNET_RPC_URL || "https://arb1.arbitrum.io/rpc",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_ARBITRUM_MAINNET_BLOCK_EXPLORER || "https://arbiscan.io",
    ],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  // Distinct from `ethereum` (Sepolia) — never interchange them.
  ethereum_mainnet: {
    chainId: "0x1", // 1 in hex (Ethereum Mainnet)
    chainName: "Ethereum",
    rpcUrls: [
      process.env.REACT_APP_ETHEREUM_MAINNET_RPC_URL ||
        "https://ethereum-rpc.publicnode.com",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_ETHEREUM_MAINNET_BLOCK_EXPLORER || "https://etherscan.io",
    ],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  // Distinct from `base` (Base Sepolia) — never interchange them.
  base_mainnet: {
    chainId: "0x2105", // 8453 in hex (Base Mainnet)
    chainName: "Base",
    rpcUrls: [
      // Same default as chains.js — avoid mainnet.base.org 429s during setup.
      process.env.REACT_APP_BASE_MAINNET_RPC_URL ||
        "https://base-rpc.publicnode.com",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_BASE_MAINNET_BLOCK_EXPLORER || "https://basescan.org",
    ],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
  robinhood_mainnet: {
    chainId: "0x1237", // 4663 in hex (Robinhood Chain Mainnet)
    chainName: "Robinhood Chain",
    rpcUrls: [
      process.env.REACT_APP_ROBINHOOD_MAINNET_RPC_URL ||
        "https://rpc.mainnet.chain.robinhood.com",
    ],
    blockExplorerUrls: [
      process.env.REACT_APP_ROBINHOOD_MAINNET_BLOCK_EXPLORER ||
        "https://robinhoodchain.blockscout.com",
    ],
    nativeCurrency: {
      name: "Ethereum",
      symbol: "ETH",
      decimals: 18,
    },
  },
};

const rebuildContractForActiveChain = async ({
  activeProvider,
  updateProvider,
  updateSigner,
  updateContract,
  updateChainId,
  updateChainPreference,
  preferredChainKey,
}) => {
  if (!activeProvider) {
    console.warn(
      "[rebuildContractForActiveChain] No active provider available",
    );
    return;
  }

  try {
    const preferredCfg =
      preferredChainKey && CHAINS[preferredChainKey]
        ? CHAINS[preferredChainKey]
        : null;
    const expectedChainId = preferredCfg?.chainIdDecimal || null;
    // Point hybrid reads at the chain the UI just selected *before*
    // getNetwork(), so a wrap-time mainnet RPC can't keep reporting 42161
    // after the wallet switched to Sepolia (421614).
    if (expectedChainId) {
      retargetHybridChain(activeProvider, expectedChainId);
    }

    const ethersProvider = new ethers.providers.Web3Provider(
      activeProvider,
      "any",
    );
    const signer = ethersProvider.getSigner();
    let network = await ethersProvider.getNetwork();
    let chainIdNum = Number(network.chainId);

    // After wallet_switchEthereumChain, getNetwork() can lag briefly and still
    // report the previous chain. Never attach the preferred chain's contract
    // address to a provider that is still on the old network — that produces
    // CALL_EXCEPTION with data="0x" on every eth_call (balanceOf,
    // getAccountCore, getPendingAction, …). Wait for the wallet to catch up;
    // if it never does, fall back to whatever chain the wallet actually is on.
    if (expectedChainId && chainIdNum !== expectedChainId) {
      for (let attempt = 0; attempt < 12; attempt++) {
        await new Promise((r) => setTimeout(r, 150));
        network = await ethersProvider.getNetwork();
        chainIdNum = Number(network.chainId);
        if (chainIdNum === expectedChainId) break;
      }
    }

    const preferredMatches =
      !!preferredCfg && preferredCfg.chainIdDecimal === chainIdNum;
    const resolved = resolveChainEntry(chainIdNum, {
      preferredKey: preferredMatches ? preferredChainKey : null,
    });
    const chainKey = preferredMatches
      ? preferredChainKey
      : resolved?.key || null;
    const chainCfg = chainKey ? CHAINS[chainKey] : resolved?.chain || null;

    updateProvider(ethersProvider);
    updateSigner(signer);
    updateChainId(chainCfg?.chainIdDecimal || chainIdNum);

    if (chainCfg?.contractAddress) {
      const newContract = new ethers.Contract(
        chainCfg.contractAddress,
        CONTRACT_ABI,
        signer,
      );
      updateContract(newContract);
    } else if (preferredCfg?.contractAddress && preferredCfg?.rpcUrl) {
      // Wallet is still on a different EVM id than the UI selection (e.g.
      // still on 42161 while the UI is on Arbitrum Sepolia 421614). Keep a
      // read contract for the *selected* chain so balances aren't forced to 0.
      const readContract = new ethers.Contract(
        preferredCfg.contractAddress,
        CONTRACT_ABI,
        new ethers.providers.JsonRpcProvider(preferredCfg.rpcUrl),
      );
      updateContract(readContract);
      updateChainId(preferredCfg.chainIdDecimal);
    } else {
      updateContract(null);
      if (!preferredCfg?.contractAddress) {
        console.warn(
          "[rebuildContractForActiveChain] No contract address configured for chainId",
          chainIdNum,
        );
      }
    }

    if (preferredMatches && chainKey) {
      updateChainPreference(chainKey);
    } else if (!preferredChainKey && chainKey) {
      updateChainPreference(chainKey);
    }
  } catch (error) {
    console.error(
      "[rebuildContractForActiveChain] Failed to rebuild contract:",
      error,
    );
  }
};

// Legacy constant for backward compatibility
const ARBITRUM_SEPOLIA = NETWORK_CONFIGS.arbitrum;

// Helper to convert decimal chain ID to hex
const toHexChainId = (dec) => "0x" + Number(dec).toString(16);

// Helper to pick network config by decimal chain ID
const pickNetworkConfigByDecimal = (dec) => {
  return (
    Object.values(NETWORK_CONFIGS).find((config) => {
      if (!config?.chainId) return false;
      const parsed = parseInt(config.chainId, 16);
      return !Number.isNaN(parsed) && parsed === dec;
    }) || null
  );
};

// Helper to resolve RPC URL based on chain ID
const resolveRpcUrl = (decChainId) => {
  const config = pickNetworkConfigByDecimal(decChainId);
  return config?.rpcUrls?.[0] || null;
};

// Helper to check if a value is zero (handles BigNumber, string, number)
const isZeroish = (v) => {
  if (v == null) return true;
  try {
    return ethers.BigNumber.from(v).isZero();
  } catch {
    return Number(v) === 0;
  }
};

// Helper to format units (handles BigNumber, string, number)
const fmt = (v, dec = 18) => {
  try {
    return ethers.utils.formatUnits(v, dec);
  } catch {
    return String(v);
  }
};

// Helper function to detect if running on mobile device
const isMobileDevice = () => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    ) ||
    (typeof window !== "undefined" && window.innerWidth <= 768)
  );
};

// ---- BLOCKLIST: ban Phantom EVM provider everywhere ----
const isPhantomProvider = (prov) => {
  if (!prov) return false;

  // direct flags commonly set by Phantom
  if (prov.isPhantom || prov.isPhantomEvm || prov.isPhantomEthereum)
    return true;

  // EIP-6963 metadata (some libs expose info/_info)
  const rdns = prov.info?.rdns || prov._info?.rdns || "";
  const name = prov.info?.name || prov._info?.name || "";
  if (/phantom/i.test(rdns) || /phantom/i.test(name)) return true;

  // stringify fallback
  const s = (prov.toString?.() || "").toLowerCase();
  return s.includes("phantom");
};

// Remember which wallet was last active across page reloads
const WALLET_STORAGE_KEY = "stabletrust:lastWalletOrigin";
// SessionStorage key to track if tab is still open (persists on refresh, clears on tab close)
const WALLET_SESSION_KEY = "stabletrust:walletTabActive";
// LocalStorage key to track if user explicitly disconnected (persists across refreshes)
const WALLET_DISCONNECTED_KEY = "stabletrust:walletDisconnected";
// Per-address marker: first successful connect redirects to the Deposit tab once
const FIRST_CONNECT_DEPOSIT_KEY_PREFIX = "stabletrust:firstConnectDeposit:";
// Matches secureKeyCache connection history keys (localStorage)
const SECURE_CACHE_CONNECTED_PREFIX = "ConfidentialBalances_connected_";

const hasSecureCacheConnectionHistory = (address) => {
  if (!address || typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  try {
    const needle = `${SECURE_CACHE_CONNECTED_PREFIX}${address.toLowerCase()}-`;
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(needle)) return true;
    }
    return false;
  } catch {
    return false;
  }
};

/** Returns true once per wallet address (localStorage), then marks it as seen. */
const shouldRedirectToDepositOnFirstConnect = (address) => {
  if (!address || typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  try {
    const key = `${FIRST_CONNECT_DEPOSIT_KEY_PREFIX}${address.toLowerCase()}`;
    if (window.localStorage.getItem(key)) return false;

    // Already finished first-connect UX before this redirect shipped
    if (hasSecureCacheConnectionHistory(address)) {
      window.localStorage.setItem(key, "true");
      return false;
    }

    window.localStorage.setItem(key, "true");
    return true;
  } catch (e) {
    console.warn("[FirstConnect] Failed to read/set first-connect marker:", e);
    return false;
  }
};

const persistLastWallet = (origin) => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    if (!origin) {
      window.localStorage.removeItem(WALLET_STORAGE_KEY);
    } else {
      window.localStorage.setItem(WALLET_STORAGE_KEY, origin);
    }
  } catch (e) {
    console.warn("[WalletPersist] Failed to persist last wallet origin:", e);
  }
};

const readLastWallet = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    const origin = window.localStorage.getItem(WALLET_STORAGE_KEY);
    return origin || null;
  } catch {
    return null;
  }
};

const clearLastWallet = () => {
  persistLastWallet(null);
};

// Set sessionStorage flag to indicate wallet is connected in this tab
// This flag persists on page refresh but clears when tab is closed
const setWalletTabActive = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(WALLET_SESSION_KEY, "true");
  } catch (e) {
    console.warn("[WalletTab] Failed to set session flag:", e);
  }
};

// Check if the tab is still active (was not closed)
const isWalletTabActive = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return false;
  try {
    const isActive =
      window.sessionStorage.getItem(WALLET_SESSION_KEY) === "true";
    return isActive;
  } catch {
    return false;
  }
};

// Clear sessionStorage flag when wallet disconnects
const clearWalletTabActive = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(WALLET_SESSION_KEY);
  } catch (e) {
    console.warn("[WalletTab] Failed to clear session flag:", e);
  }
};

// Set localStorage flag to indicate user explicitly disconnected
const setWalletDisconnectedFlag = () => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(WALLET_DISCONNECTED_KEY, "true");
  } catch (e) {
    console.warn("[WalletDisconnect] Failed to set disconnected flag:", e);
  }
};

// Check if user explicitly disconnected
const isWalletDisconnected = () => {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    const isDisconnected =
      window.localStorage.getItem(WALLET_DISCONNECTED_KEY) === "true";
    return isDisconnected;
  } catch {
    return false;
  }
};

// Clear disconnected flag when wallet connects
const clearWalletDisconnectedFlag = () => {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.removeItem(WALLET_DISCONNECTED_KEY);
  } catch (e) {
    console.warn("[WalletDisconnect] Failed to clear disconnected flag:", e);
  }
};

// A Privy session deliberately outlives an app-level "Disconnect" so that
// reconnecting is instant. That makes it essential to show *which* identity is
// signed in — otherwise the next connect silently reuses the previous account
// with nothing in the UI saying so. Returns null when nothing is displayable.
const getPrivyIdentityLabel = (user) => {
  if (!user) return null;

  const direct =
    user.email?.address ||
    user.google?.email ||
    user.phone?.number ||
    user.apple?.email ||
    user.twitter?.username ||
    user.github?.username;
  if (typeof direct === "string" && direct) return direct;

  // Fall back to the linked-account list for login methods that are not spread
  // onto the top-level `user` object. Wallets are skipped: the address is
  // already shown next to this label, so it names nothing new.
  const linked = Array.isArray(user.linkedAccounts) ? user.linkedAccounts : [];
  for (const account of linked) {
    if (account?.type === "wallet" || account?.type === "smart_wallet") continue;
    const label =
      account?.address || account?.email || account?.number || account?.username;
    if (typeof label === "string" && label) return label;
  }

  // Passkey-only users have no addressable identifier — name the method instead.
  if (linked.some((account) => account?.type === "passkey")) return "Passkey";

  return null;
};

// ── Privy wallet resolution ──────────────────────────────────────────────
// Privy hands back every wallet the user has connected: the embedded one it
// provisions for email/social/passkey logins, and any external wallet
// (MetaMask, Coinbase, Rainbow, WalletConnect) the user signed in with. Both
// kinds implement the same interface — `getEthereumProvider()`, `switchChain()`,
// `address` — so once the right one is picked the app treats them identically.

/** True for the wallet Privy generates and custodies for the user. */
const isEmbeddedPrivyWallet = (w) => {
  if (!w) return false;
  if (w.isEmbedded) return true;
  // Embedded wallets report `privy` / `privy-v2` as their client type.
  const clientType = w.walletClientType?.toLowerCase?.() || "";
  if (clientType.includes("privy")) return true;
  const connectorType = (
    w.connectorType ||
    w.walletConnector?.type ||
    ""
  ).toLowerCase?.();
  return connectorType === "privy" || connectorType === "embedded";
};

/** Addresses of every wallet the authenticated Privy user has linked. */
const linkedWalletAddresses = (user) => {
  const addresses = new Set();
  const add = (address) => {
    if (typeof address === "string" && address) {
      addresses.add(address.toLowerCase());
    }
  };
  add(user?.wallet?.address);
  const linked = Array.isArray(user?.linkedAccounts) ? user.linkedAccounts : [];
  linked.forEach((account) => {
    if (account?.type === "wallet") add(account.address);
  });
  return addresses;
};

/**
 * True when the user's first verified wallet — the one they signed up with —
 * is an external wallet (MetaMask, Rainbow, …) rather than one Privy made.
 * Privy's `user.wallet` is that first wallet. Email/Google/passkey users get
 * their embedded wallet at signup, so it stays first for them even if they
 * link an extension later.
 */
const signedUpWithExternalWallet = (user) => {
  const primary = user?.wallet;
  if (!primary?.address) return false;
  return !(primary.walletClientType || "").toLowerCase().includes("privy");
};

/**
 * Pick the wallet to transact with out of Privy's list.
 *
 * The embedded wallet wins whenever the user has one. That is deliberate and
 * load-bearing: a user's confidential account — and the ElGamal keypair derived
 * from it — is tied to a specific address, so anyone who signed up with
 * email/Google/passkey must keep resolving to the same embedded address even if
 * an extension wallet happens to be connected in the same browser.
 *
 * Users who signed up with an external wallet use that wallet — most recently
 * connected first — even if an embedded wallet got linked to them later (a
 * connect that ran while the extension was down used to provision one). Their
 * account lives on the external address, so while it's unavailable this
 * returns null instead of falling back to the embedded wallet.
 *
 * Only wallets *linked to this Privy user* count. `useWallets()` also reports
 * extensions that merely happen to be connected in the browser, and wiring one
 * of those would sign as a stranger's address for a user who logged in with
 * Google.
 */
const resolvePrivyWallet = (wallets, user) => {
  const list = Array.isArray(wallets) ? wallets : [];
  if (list.length === 0) return null;

  const embedded = list.find(isEmbeddedPrivyWallet);
  if (embedded && !signedUpWithExternalWallet(user)) return embedded;

  const linkedAddresses = linkedWalletAddresses(user);
  const external = list
    .filter(
      (w) =>
        !isEmbeddedPrivyWallet(w) &&
        typeof w?.getEthereumProvider === "function" &&
        (w.linked === true ||
          linkedAddresses.has((w.address || "").toLowerCase())),
    )
    .sort((a, b) => (b?.connectedAt || 0) - (a?.connectedAt || 0));

  return external[0] || null;
};

/**
 * True when the user's Privy record says they own an embedded wallet.
 *
 * `useWallets()` hydrates asynchronously, and an injected extension can land in
 * the list before the embedded wallet does. Wiring that first arrival would
 * connect an existing user to the wrong address — so this lets the connect
 * paths hold out for the embedded wallet whenever the account has one.
 */
const hasLinkedEmbeddedWallet = (user) => {
  if (!user) return false;
  const isPrivyClient = (clientType) =>
    (clientType || "").toLowerCase().includes("privy");
  if (isPrivyClient(user.wallet?.walletClientType)) return true;
  const linked = Array.isArray(user.linkedAccounts) ? user.linkedAccounts : [];
  return linked.some(
    (account) =>
      account?.type === "wallet" && isPrivyClient(account?.walletClientType),
  );
};

/** Whether a connect should wait for the embedded wallet instead of using another. */
const shouldHoldOutForEmbeddedWallet = (user) =>
  hasLinkedEmbeddedWallet(user) && !signedUpWithExternalWallet(user);

/** Human-readable name for a Privy wallet, for notifications. */
const describePrivyWallet = (wallet) => {
  if (!wallet) return "wallet";
  if (isEmbeddedPrivyWallet(wallet)) return "Privy";
  return wallet.meta?.name || wallet.walletClientType || "your wallet";
};

function useAppState() {
  const state = {
    wasmModule: useState(null),
    provider: useState(null),
    signer: useState(null),
    contract: useState(null),
    userAccount: useState(null),
    hasActiveAccount: useState(false),
    accountExists: useState(false),
    accountData: useState(null),
    notifications: useState([]),
    keyBannerVisible: useState(false),
    derivedKeys: useState(null),
    isAuthenticated: useState(false),
    confidentialBalance: useState(null),
    // First-time / disconnected users land on Deposit; funded users are
    // routed to Transfer once available balance is known (see auto-route effect).
    activeTab: useState("deposit"),
    bannerVisible: useState(true),
    currentTokenData: useState(null),
  };
  return state;
}

function App() {
  // Chain/token selection now persists across page refreshes
  // (previously had one-time cleanup that removed selectedChainId - now keeping it)
  const {
    wasmModule: [wasmModule, setWasmModule],
    provider: [provider, setProvider],
    signer: [signer, setSigner],
    contract: [contract, setContract],
    userAccount: [userAccount, setUserAccount],
    hasActiveAccount: [hasActiveAccount, setHasActiveAccount],
    accountExists: [accountExists, setAccountExists],
    accountData: [accountData, setAccountData],
    notifications: [notifications, setNotifications],
    keyBannerVisible: [keyBannerVisible, setKeyBannerVisible],
    derivedKeys: [derivedKeys, setDerivedKeys],
    isAuthenticated: [isAuthenticated, setIsAuthenticated],
    confidentialBalance: [confidentialBalance, setConfidentialBalance],
    activeTab: [activeTab, setActiveTab],
    bannerVisible: [bannerVisible, setBannerVisible],
    currentTokenData: [currentTokenData, setCurrentTokenData],
  } = useAppState();

  const [pendingBalance, setPendingBalance] = useState(null);
  const [pendingBalanceBannerDismissed, setPendingBalanceBannerDismissed] =
    useState(false);
  // Balances-and-gas: the one balance (native gas) that never had a surface
  // before, even though it's what actually blocks a transfer. Reads through
  // currentTokenData's chain, same chain the Deposit/Transfer forms use.
  const [balancesOpen, setBalancesOpen] = useState(false);
  const balances = useBalances({
    provider,
    account: userAccount,
    // Prefer the live selection from TabbedOperations; fall back to the
    // persisted chain so USDC/USDT wallet rows load before the first
    // onTokenDataChange lands.
    chainKey: currentTokenData?.chainId || getSavedChainId() || null,
  });
  const openBalances = useCallback(() => setBalancesOpen(true), []);
  const closeBalances = useCallback(() => setBalancesOpen(false), []);
  const prevPendingBalanceRef = useRef(null);
  // One-shot per wallet: route to Deposit (zero balance) or Transfer (funded)
  // after available balance loads. Avoids yanking the user if they switch tabs.
  const didAutoRouteTabRef = useRef(false);
  const [pendingActionKind, setPendingActionKind] = useState(null); // Track pending action kind: 2=deposit, 3=transfer, 5=withdraw
  const [onSecureClaimClick, setOnSecureClaimClick] = useState(null);
  const [selectTokenFn, setSelectTokenFn] = useState(null);
  const [openRequestModalFn, setOpenRequestModalFn] = useState(null);
  const [secureClaimInFlightByKey, setSecureClaimInFlightByKey] = useState({});
  const [currentChainId, setCurrentChainId] = useState(null);
  // Shared wallet selection modal state
  const [showWalletSelectionModal, setShowWalletSelectionModal] =
    useState(false);
  const [availableWalletsForModal, setAvailableWalletsForModal] =
    useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Same (max-width: 767px) check TabbedOperations uses to decide whether to
  // render MobileShell. MobileShell is a full-screen (100dvh) presentation —
  // it already draws its own header and bottom nav — so this app-level
  // header/footer need to step aside on mobile instead of stacking above and
  // below it. Left in place they simply add their height on top of a box
  // that's already exactly one viewport tall, pushing MobileShell's own
  // bottom nav below the fold and forcing a scroll to reach it.
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 767px)");
    const handleChange = (e) => setIsMobileViewport(e.matches);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // --- Invite Gate Access Control (Firestore userSiteAccess; localStorage alone is not trusted) ---
  // Disabled via INVITE_GATE_ENABLED; skipped in local dev when re-enabled.
  const [hasAccess, setHasAccess] = useState(
    !INVITE_GATE_ENABLED || process.env.NODE_ENV === "development",
  );

  const handleAccessGranted = useCallback(() => {
    setHasAccess(true);
  }, []);


  const [pendingRequest, setPendingRequest] = useState(() => {
    try {
      const s = sessionStorage.getItem("stabletrust:transferRequest");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });
  const [requestAccepted, setRequestAccepted] = useState(() => {
    try {
      return sessionStorage.getItem("stabletrust:transferRequestAccepted") === "1";
    } catch {
      return false;
    }
  });
  const openWalletRef = useRef(null);
  const requestSignInTriggeredRef = useRef(false);

  // Keep a live handle to the connect flow (defined later in the component body).
  useEffect(() => {
    openWalletRef.current = openWalletSelectionModal;
  });

  // On load: read the request link, persist it, target its chain, clean the URL.
  useEffect(() => {
    const req = parseTransferRequestFromUrl();
    if (!req) return;
    setPendingRequest(req);
    try {
      sessionStorage.setItem("stabletrust:transferRequest", JSON.stringify(req));
    } catch {}
    if (req.chainKey) setSavedChainId(req.chainKey); // so post-login lands on this chain
    clearTransferRequestFromUrl();  }, []);

  const clearPendingRequest = useCallback(() => {
    setPendingRequest(null);
    setRequestAccepted(false);
    requestSignInTriggeredRef.current = false;
    try {
      sessionStorage.removeItem("stabletrust:transferRequest");
      sessionStorage.removeItem("stabletrust:transferRequestAccepted");
    } catch {}
  }, []);

  // Payer tapped "Pay" in the request popup: remember acceptance, target the
  // request's chain, and let the sign-in effect below take over. The transfer form
  // prefills once authenticated (see incomingRequest gating).
  const acceptPaymentRequest = useCallback(() => {
    if (pendingRequest?.chainKey) setSavedChainId(pendingRequest.chainKey);
    setRequestAccepted(true);
    try {
      sessionStorage.setItem("stabletrust:transferRequestAccepted", "1");
    } catch {}
  }, [pendingRequest]);

  const declinePaymentRequest = useCallback(() => {
    clearPendingRequest();
  }, [clearPendingRequest]);

  // Track if we're in the process of connecting to a specific wallet (to prevent auto-connect)
  const isConnectingToWalletRef = useRef(false);
  /** True only while Privy's `login()` modal promise is pending cleared in `login` finally so Connect works right after dismiss (X). */
  const privyLoginModalInFlightRef = useRef(false);
  /** True while wiring the embedded wallet after auth (waits, chain switch, ethers) blocks duplicate full connects. */
  const privyWalletHookupInFlightRef = useRef(false);
  /** Bumped on each explicit Privy connect so an older connect can exit waits quietly when the user retries. */
  const privyConnectGenerationRef = useRef(0);
  /** Bumped on each Connect click for Privy so an older handleWalletSelect finally does not clear state while a newer attempt runs. */
  const privyConnectUiAttemptRef = useRef(0);
  /** True for the whole logout-then-login "switch account" round trip, which must not overlap itself. */
  const privySwitchInFlightRef = useRef(false);
  /** Render-visible mirror of the above, so the button can show progress and refuse double clicks. */
  const [privySwitching, setPrivySwitching] = useState(false);

  // Prevent any auto-connect after a cancelled Privy flow
  // Start blocked by default; only unblock after a successful connect
  const suppressAutoConnectRef = useRef(true);

  // Track the active connect target to prevent cross-wallet interference
  const CONNECT_TARGETS = {
    NONE: null,
    PRIVY: "privy",
    TRUST: "trust",
    OKX: "okx",
    COINBASE: "coinbase",
    RAINBOW: "rainbow",
  };
  const [connectTarget, setConnectTarget] = useState(CONNECT_TARGETS.NONE);
  // Refs for live values in event listeners (fixes stale closure problem)
  const connectTargetRef = useRef(connectTarget);
  useEffect(() => {
    connectTargetRef.current = connectTarget;
  }, [connectTarget]);

  const updateConnectTarget = useCallback(
    (valueOrUpdater) => {
      setConnectTarget((prev) => {
        const next =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;
        connectTargetRef.current = next;
        return next;
      });
    },
    [setConnectTarget],
  );

  const [activeWalletOrigin, setActiveWalletOrigin] = useState(null);
  const activeWalletOriginRef = useRef(activeWalletOrigin);
  useEffect(() => {
    activeWalletOriginRef.current = activeWalletOrigin;
  }, [activeWalletOrigin]);

  // Track explicit “I disconnected Privy, don’t auto-reconnect” state
  const manualPrivyDisconnectRef = useRef(false);

  const [walletConnectProvider, setWalletConnectProvider] = useState(null);
  const [activeEip1193, setActiveEip1193] = useState(null); // Track the active EIP-1193 provider
  const activeEip1193Ref = useRef(activeEip1193);
  useEffect(() => {
    activeEip1193Ref.current = activeEip1193;
  }, [activeEip1193]);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleSecureClaimInFlightChange = useCallback(
    (inFlight, claimKey) => {
      setSecureClaimInFlightByKey((prev) => {
        const next = { ...prev };
        // If no key provided, clear all in-flight secure claims for the current user
        if (!claimKey) {
          if (!userAccount) return next;
          const prefix = `${userAccount.toLowerCase()}::`;
          Object.keys(next).forEach((k) => {
            if (k.startsWith(prefix)) delete next[k];
          });
          return next;
        }
        if (inFlight) next[claimKey] = true;
        else delete next[claimKey];
        return next;
      });
    },
    [userAccount],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const showWalletSelectionModalRef = useRef(showWalletSelectionModal);
  useEffect(() => {
    showWalletSelectionModalRef.current = showWalletSelectionModal;
  }, [showWalletSelectionModal]);

  const [lastConnectedAccount, setLastConnectedAccount] = useState(null); // Track last notified account to prevent duplicates
  const [lastNotifiedChain, setLastNotifiedChain] = useState(null); // Track last notified chain to prevent duplicates

  // Privy hooks
  const {
    ready: privyReady,
    authenticated,
    login,
    logout,
    user: privyUser,
    createWallet,
    getAccessToken,
  } = usePrivy();
  const { wallets: privyWallets, ready: walletsReady } = useWallets();

  // After the payer accepts the request popup ("Pay") and is not yet signed in,
  // prompt sign-in once. (Placed after the Privy hooks so privyReady/authenticated
  // are initialized. On redirect-based login the accepted flag is restored from
  // sessionStorage, so this still fires on return.)
  useEffect(() => {
    if (!pendingRequest || !requestAccepted || !privyReady || authenticated) return;
    if (requestSignInTriggeredRef.current) return;
    requestSignInTriggeredRef.current = true;
    Promise.resolve(
      typeof openWalletRef.current === "function" ? openWalletRef.current() : null,
    ).catch(() => {});
  }, [pendingRequest, requestAccepted, privyReady, authenticated]);

  /**
   * Abort any in-flight Privy connect so a new explicit click is not a silent
   * no-op. MetaMask logout can leave hookup waits hanging while
   * `isConnectingToWalletRef` stays true — the Connect Wallet modal then
   * ignores "Continue with Privy".
   */
  const invalidateInFlightPrivyConnect = useCallback(() => {
    const hadFlight =
      isConnectingToWalletRef.current ||
      privyLoginModalInFlightRef.current ||
      privyWalletHookupInFlightRef.current;
    if (!hadFlight) return false;

    privyConnectGenerationRef.current += 1;
    privyLoginModalInFlightRef.current = false;
    privyWalletHookupInFlightRef.current = false;
    isConnectingToWalletRef.current = false;
    return true;
  }, []);

  // Which Privy identity is signed in right now (null when none / not nameable).
  const privyIdentityLabel = useMemo(() => {
    const label = getPrivyIdentityLabel(privyUser);
    if (label) return label;
    // A wallet login has no email/social account to name, so name the wallet
    // itself and the account menu can still say who is signed in.
    const wallet = resolvePrivyWallet(privyWallets, privyUser);
    if (wallet && !isEmbeddedPrivyWallet(wallet)) {
      return describePrivyWallet(wallet);
    }
    return null;
  }, [privyUser, privyWallets]);

  // Track Privy readiness in a ref so async code sees updates
  const privyReadyRef = useRef(privyReady);
  useEffect(() => {
    privyReadyRef.current = privyReady;
  }, [privyReady]);

  const privyWalletsRef = useRef(privyWallets);
  useEffect(() => {
    privyWalletsRef.current = privyWallets;
  }, [privyWallets]);
  const privyUserRef = useRef(privyUser);
  useEffect(() => {
    privyUserRef.current = privyUser;
  }, [privyUser]);
  const authenticatedRef = useRef(authenticated);
  useEffect(() => {
    authenticatedRef.current = authenticated;
  }, [authenticated]);
  const walletsReadyRef = useRef(walletsReady);
  useEffect(() => {
    walletsReadyRef.current = walletsReady;
  }, [walletsReady]);
  const createWalletRef = useRef(createWallet);
  useEffect(() => {
    createWalletRef.current = createWallet;
  }, [createWallet]);
  // Privy hook fns can close over the pre-logout `authenticated` flag. Async
  // flows (especially "sign in as someone else") must call the latest ones —
  // otherwise `login()` is a silent no-op and the user is left disconnected.
  const loginRef = useRef(login);
  const logoutRef = useRef(logout);
  const getAccessTokenRef = useRef(getAccessToken);
  useEffect(() => {
    loginRef.current = login;
    logoutRef.current = logout;
    getAccessTokenRef.current = getAccessToken;
  }, [login, logout, getAccessToken]);
  const handleWalletSelectRef = useRef(null);

  // Dismiss the initial page loader (defined in public/index.html) once Privy
  // has finished initializing. Falls back to a hard timeout so the loader can
  // never get stuck if Privy hangs (e.g. offline / misconfigured env).
  useEffect(() => {
    const markAppReady = () => {
      if (
        typeof document !== "undefined" &&
        !document.body.classList.contains("app-ready")
      ) {
        document.body.classList.add("app-ready");
        const loader = document.getElementById("app-loader");
        if (loader) {
          // Remove from DOM after the CSS fade-out completes
          setTimeout(() => {
            loader.parentNode && loader.parentNode.removeChild(loader);
          }, 700);
        }
      }
    };

    if (privyReady) {
      // Small delay lets the UI paint one frame before the loader fades,
      // avoiding a visible flash of grey placeholders.
      const t = setTimeout(markAppReady, 120);
      return () => clearTimeout(t);
    }

    // Safety net: never block the UI behind the loader for more than 6s
    const failSafe = setTimeout(markAppReady, 6000);
    return () => clearTimeout(failSafe);
  }, [privyReady]);

  // Track previous Privy authenticated value so we can detect the moment
  // the user goes from "logged out" -> "logged in" and auto-wire the wallet.
  const prevAuthenticatedRef = useRef(false);

  // Refs to store functions so they can be accessed before they're defined
  const checkConfidentialAccountRef = useRef(null);
  const setWalletConnectedWithProviderRef = useRef(null);
  const setWalletDisconnectedRef = useRef(null);
  const setWalletConnectedRef = useRef(null);
  const userAccountRef = useRef(null);

  const providerRef = useRef(null);
  useEffect(() => {
    providerRef.current = provider;
  }, [provider]);

  const signerRef = useRef(null);
  useEffect(() => {
    signerRef.current = signer;
  }, [signer]);

  // Centralized provider event handler registry
  const providerHandlersRef = useRef(new Map());

  // Global guard: allow connection only if not suppressed or explicit target matches origin
  const allowConnectNow = useCallback((origin) => {
    if (!suppressAutoConnectRef.current) {
      return true;
    }

    const activeTarget = connectTargetRef.current;
    if (!activeTarget) {
      return false;
    }

    const map = {
      privy: CONNECT_TARGETS.PRIVY,
      trust: CONNECT_TARGETS.TRUST,
      okx: CONNECT_TARGETS.OKX,
      coinbase: CONNECT_TARGETS.COINBASE,
      rainbow: CONNECT_TARGETS.RAINBOW,
    };

    return map[origin] === activeTarget;
  }, []);

  // Register event handlers for a provider (with suppression guards)
  const registerHandlers = useCallback(
    (prov, handlers, origin) => {
      if (!prov?.on) return;

      // Wrap handlers to respect suppression (except disconnect, which always runs for cleanup)
      const wrap = (fn) =>
        fn
          ? (...args) => {
              if (!allowConnectNow(origin)) {
                return;
              }
              try {
                fn(...args);
              } catch (e) {
                console.error("handler error", e);
              }
            }
          : undefined;

      const wrapped = {
        accountsChanged: wrap(handlers.accountsChanged),
        chainChanged: wrap(handlers.chainChanged),
        // allow disconnect without the guard so we always clean up
        disconnect: handlers.disconnect,
      };

      if (wrapped.accountsChanged)
        prov.on("accountsChanged", wrapped.accountsChanged);
      if (wrapped.chainChanged) prov.on("chainChanged", wrapped.chainChanged);
      if (wrapped.disconnect) prov.on("disconnect", wrapped.disconnect);

      // keep the *wrapped* refs so we can remove the exact functions later
      providerHandlersRef.current.set(prov, wrapped);
    },
    [allowConnectNow],
  );

  // Unregister event handlers for a provider
  const unregisterHandlers = useCallback((prov) => {
    if (!prov?.removeListener) return;
    const wrapped = providerHandlersRef.current.get(prov);
    if (!wrapped) return;
    if (wrapped.accountsChanged)
      prov.removeListener("accountsChanged", wrapped.accountsChanged);
    if (wrapped.chainChanged)
      prov.removeListener("chainChanged", wrapped.chainChanged);
    if (wrapped.disconnect)
      prov.removeListener("disconnect", wrapped.disconnect);
    providerHandlersRef.current.delete(prov);
  }, []);

  // Get WalletConnect Project ID from environment variable
  const WALLETCONNECT_PROJECT_ID =
    process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;

  // Trigger EIP-6963 discovery early so cache gets populated before first connect
  useEffect(() => {
    // Fire-and-forget; this populates _announced cache
    discoverEip6963Providers(1200).catch(() => {});
  }, []);

  // Helper function to check if we're using Privy and reconnect if needed
  // Defined early so it can be used in useEffect and other functions
  // Note: checkConfidentialAccount is referenced but defined later, so we'll access it via closure
  // This function can now do a first connection, not only "reconnect after user already had a wallet attached"
  const ensurePrivyConnection = useCallback(async () => {
    // 🔒 Do NOT auto-reconnect Privy if the user explicitly disconnected it
    if (manualPrivyDisconnectRef.current) {
      return false;
    }

    // 🔒 Do NOT auto-reconnect if user explicitly disconnected (persists across refreshes)
    if (isWalletDisconnected()) {
      return false;
    }

    // 🔒 Do NOT auto-reconnect if tab was closed and reopened (only reconnect on refresh)
    // Note: First-time login is handled by the auto-connect effect which sets setWalletTabActive()
    // when justLoggedIn is detected, so by the time this function is called, the flag should be set.
    if (!isWalletTabActive()) {
      return false;
    }

    // Only run when Privy is ready, user is logged in, and wallets are loaded
    if (
      !privyReady ||
      !authenticated ||
      !privyWallets ||
      privyWallets.length === 0
    ) {
      return false;
    }

    // Same resolution as connectWithPrivy: the embedded wallet when the user
    // has one, otherwise the external wallet they logged in with. Picking it
    // the same way in both places is what keeps a refresh landing on the very
    // same address the explicit connect used.
    const privyWallet = resolvePrivyWallet(privyWallets, privyUser);
    if (!privyWallet) {
      return false;
    }

    // Hold out for the embedded wallet when the account has one: an injected
    // extension can populate the list first, and connecting that address would
    // strand the user's confidential account. The effects below re-run when the
    // wallet list changes, so returning false here just defers a beat.
    if (!isEmbeddedPrivyWallet(privyWallet) && shouldHoldOutForEmbeddedWallet(privyUser)) {
      return false;
    }

    // Never silently re-wire a wallet that only claims to be the one the user
    // connected. No notification here: this runs in the background, and the
    // explicit connect flow reports the mismatch properly when they click
    // Connect. Blocking auto-reconnect is what stops the effects retrying.
    if (
      !isEmbeddedPrivyWallet(privyWallet) &&
      privyWallet.connectorType === "injected"
    ) {
      const { ok } = await verifyInjectedWalletIdentity(
        privyWallet.walletClientType,
      );
      if (!ok) {
        manualPrivyDisconnectRef.current = true;
        return false;
      }
    }

    // If we already have a wired provider/signer with the same account, nothing to do
    if (userAccount && provider && signer) {
      try {
        const accounts = await provider.listAccounts();
        if (
          accounts.length > 0 &&
          accounts[0].toLowerCase() === userAccount.toLowerCase()
        ) {
          return true;
        }
      } catch (e) {
      }
    }

    // --- existing connection logic stays the same from here down ---
    try {
      // Ensure correct chain only if user has selected one
      const selected = getSavedChainId();
      if (selected && NETWORK_CONFIGS[selected]?.chainId) {
        const targetDec = parseInt(NETWORK_CONFIGS[selected].chainId, 16);
        try {
          await privyWallet.switchChain(targetDec);
        } catch (switchError) {
          // Handle chain switching errors, especially for unsupported chains
          if (
            switchError?.message?.includes("Unsupported chainId") ||
            switchError?.message?.includes("unsupported chain")
          ) {
            const chainName = NETWORK_CONFIGS[selected]?.chainName || selected;
            console.error(
              `[Privy] Chain switch failed: ${switchError.message}`,
            );
            showNotification(
              `Privy does not support ${chainName}. Please use a different wallet or chain.`,
              "error",
            );
            throw switchError;
          }
          throw switchError;
        }
      }

      // Get EIP-1193 provider and wrap with ethers
      // Wrap it so reads/estimateGas are served by a reliable public RPC (Alchemy)
      const rawEip1193 = await privyWallet.getEthereumProvider();
      const eip1193 = makeHybridEip1193(
        rawEip1193,
        (selected && NETWORK_CONFIGS[selected]?.chainId) || currentChainId,
      );
      const newProvider = new ethers.providers.Web3Provider(eip1193, "any");
      const newSigner = newProvider.getSigner();
      const accounts = await newProvider.listAccounts();
      if (!accounts?.length) {
        return false;
      }

      setActiveEip1193(eip1193);
      // Always wire the embedded wallet, even on the very first connection.
      // Call setWalletConnectedWithProvider directly instead of through the ref,
      // so it works even when the ref hasn't been set yet (first connection).
      await setWalletConnectedWithProvider(
        accounts[0],
        newProvider,
        newSigner,
        "privy",
        { forceAllow: true }, // Allow Privy to connect even when auto-connect is suppressed
      );

      // Register handlers for future account/chain changes
      registerHandlers(
        eip1193,
        {
          accountsChanged: (accs) => {
            if (accs.length === 0) {
              setWalletDisconnectedRef.current?.();
              return;
            }
            const currentProvider = newProvider;
            const currentSigner = newSigner;
            if (currentProvider && currentSigner) {
              setWalletConnectedWithProviderRef.current?.(
                accs[0],
                currentProvider,
                currentSigner,
                "privy",
              );
            } else {
              setWalletConnectedRef.current?.(accs[0], "privy");
            }
          },
          chainChanged: (chainId) => {
            const newChainId = parseInt(chainId, 16);
            setCurrentChainId(newChainId);
            setTimeout(() => {
              checkConfidentialAccountRef.current?.();
            }, 1000);
          },
        },
        "privy",
      );

      setTimeout(() => {
        checkConfidentialAccountRef.current?.();
      }, 500);

      return true;
    } catch (error) {
      console.error("Failed to reconnect Privy:", error);
      return false;
    }
  }, [
    privyReady,
    authenticated,
    privyWallets,
    privyUser,
    userAccount,
    provider,
    signer,
    registerHandlers,
  ]);

  // Monitor Privy wallet state and auto-reconnect if needed (especially after account creation)
  useEffect(() => {
    // Only run if Privy is ready and authenticated
    if (!privyReady || !authenticated || !walletsReady) {
      return;
    }

    // Check if we have Privy wallets but no active connection
    if (privyWallets && privyWallets.length > 0) {
      // If we have a userAccount but no provider/signer, we need to reconnect
      if (userAccount && (!provider || !signer)) {
        // Small delay to avoid race conditions
        const timeoutId = setTimeout(() => {
          ensurePrivyConnection().catch((err) => {
            console.error("Auto-reconnect failed:", err);
          });
        }, 500);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    privyReady,
    authenticated,
    walletsReady,
    privyWallets,
    userAccount,
    provider,
    signer,
    ensurePrivyConnection,
  ]);

  // After Privy auth completes (Google/email/passkey), automatically wire the embedded wallet
  // so that "login with Privy" is a single step: auth + embedded wallet connected.
  //
  // IMPORTANT: we now auto-connect in ANY state where:
  //   - privyReady && walletsReady && authenticated
  //   - and there is NO userAccount wired yet.
  //
  // That covers both:
  //   - "just logged in now", and
  //   - "was already logged in when the page loaded".
  useEffect(() => {
    const prevAuth = prevAuthenticatedRef.current;

    const updatePrev = () => {
      prevAuthenticatedRef.current = authenticated;
    };

    // We need the Privy SDK and wallet list to be ready
    if (!privyReady || !walletsReady) {
      updatePrev();
      return;
    }

    // If we're not authenticated with Privy, nothing to do
    if (!authenticated) {
      updatePrev();
      return;
    }

    // If connectWithPrivy() is actively running, let it handle the connection
    // Don't interfere with the explicit connection flow
    if (
      privyLoginModalInFlightRef.current ||
      privyWalletHookupInFlightRef.current
    ) {
      updatePrev();
      return;
    }

    // 🔒 Do not auto-wire the embedded wallet if the user explicitly disconnected it
    if (manualPrivyDisconnectRef.current) {
      updatePrev();
      return;
    }

    // 🔒 Do NOT auto-reconnect if user explicitly disconnected (persists across refreshes)
    // BUT: if we just authenticated (first-time signup), allow connection
    const justLoggedIn = authenticated && !prevAuth;
    if (isWalletDisconnected() && !justLoggedIn) {
      updatePrev();
      return;
    }

    // If user just logged in (first-time signup), clear the disconnect flag to allow connection
    if (justLoggedIn && isWalletDisconnected()) {
      clearWalletDisconnectedFlag();
    }

    // For first-time login, ensure the tab active flag is set early
    // This ensures ensurePrivyConnection won't be blocked
    if (justLoggedIn) {
      setWalletTabActive();
    }

    // If an EVM account is already wired, don't touch anything
    if (userAccount) {
      updatePrev();
      return;
    }

    // No Privy wallets yet (edge case) -> nothing to wire
    if (!privyWallets || privyWallets.length === 0) {
      updatePrev();
      return;
    }

    // justLoggedIn was already declared above

    // At this point we KNOW:
    //   - Privy is ready
    //   - Wallets are ready
    //   - User is authenticated
    //   - No EVM account is wired yet
    // → Always ensure the embedded wallet is connected.
    ensurePrivyConnection().catch((err) => {
      console.error("Auto Privy connect failed:", err);
    });

    updatePrev();
  }, [
    privyReady,
    walletsReady,
    authenticated,
    privyWallets,
    userAccount,
    ensurePrivyConnection,
  ]);

  // Initialize WebAssembly module
  useEffect(() => {
    const initializeWasm = async () => {
      try {
        const wasm = await init();
        setWasmModule(wasm);
      } catch (error) {
        console.error("Failed to load WebAssembly module:", error);
        showNotification(
          "WebAssembly module failed to load. Some features may not work.",
          "error",
        );
      }
    };

    initializeWasm();
  }, []);

  // Initialize Firebase Auth and Firestore-backed invite access
  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged(async (user) => {
      setIsAuthenticated(!!user);
      // When the gate is disabled or in dev, hasAccess starts true; don't let
      // Firestore access checks revoke that.
      if (!INVITE_GATE_ENABLED || process.env.NODE_ENV === "development") return;
      if (user) {
        try {
          const ok = await hasUserSiteAccessRecord();
          setHasAccess(ok);
          if (ok) {
            try {
              localStorage.setItem("stabletrust:hasAccess", "true");
            } catch {}
          }
        } catch (err) {
          console.error("Failed to verify site access record:", err);
        }
      } else {
        setHasAccess(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Auto-sign in when wallet connects
  useEffect(() => {
    if (userAccount && !isAuthenticated) {
      const signInUser = async () => {
        try {
          await AuthService.signInAnonymously();
          await AuthService.updateUserProfile(userAccount);
          // Authentication successful - no notification needed
        } catch (error) {
          console.error("Failed to authenticate:", error);
          showNotification("Failed to authenticate with Firebase", "error");
        }
      };
      signInUser();
    }
  }, [userAccount, isAuthenticated]);

  // Restore the last wallet origin on mount so provider events are allowed
  // through `allowConnectNow` before an explicit connect flow starts.
  // (This used to also attach window.ethereum listeners for the standalone
  // MetaMask path; connections now come through Privy, which registers its own
  // handlers on the wallet provider it hands back.)
  useEffect(() => {
    const lastWallet = readLastWallet();
    if (lastWallet === CONNECT_TARGETS.PRIVY) {
      connectTargetRef.current = CONNECT_TARGETS.PRIVY;
      updateConnectTarget(CONNECT_TARGETS.PRIVY);
    }
  }, []);

  // Force light theme on mount and clean up legacy preferences
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        window.localStorage.removeItem("theme");
      } catch (error) {
        console.warn("[Theme] Unable to clear legacy theme preference:", error);
      }
    }
  }, []);

  // Reset banner visibility when wallet disconnects
  useEffect(() => {
    if (!userAccount) {
      setBannerVisible(true);
    }
  }, [userAccount]);


  // Helper to set wallet connected with a custom provider
  const setWalletConnectedWithProvider = async (
    address,
    customProvider,
    customSigner,
    origin = "privy",
    options = {},
  ) => {

    // Always keep the ref updated
    setWalletConnectedWithProviderRef.current = setWalletConnectedWithProvider;

    // HARD GUARD: do nothing if user has cancelled/closed Privy and no explicit flow is active
    if (!options.forceAllow && !allowConnectNow(origin)) {
      return;
    }


    try {
      // Determine underlying EIP-1193 provider first
      const eip1193Provider = customProvider.provider ?? customProvider;

      // >>> HARD BLOCK: Phantom EVM must never connect or show
      if (isPhantomProvider(eip1193Provider)) {
        console.warn("[Guard] Blocking Phantom provider from connecting");
        showNotification(
          "Phantom (EVM) is not supported. Please sign in with Privy.",
          "error",
        );
        await setWalletDisconnected(); // ensure UI is clean
        return;
      }

      // If this is the same account and we're fully wired, skip reconnect
      if (userAccount === address && provider && signer) {
        return;
      }


      // Now it's safe to clear old state and set new account
      const previousAccount = userAccount;

      setAccountData(null);
      setHasActiveAccount(false);
      setAccountExists(false);
      setConfidentialBalance(null);
      setPendingBalance(null);
      setSecureClaimInFlightByKey({});
      setDerivedKeys(null);
      setKeyBannerVisible(false);

      setUserAccount(address);
      setActiveWalletOrigin(origin);
      persistLastWallet(origin);
      setWalletTabActive(); // Mark tab as active so wallet persists on refresh
      clearWalletDisconnectedFlag(); // Clear disconnected flag since user is now connected
      suppressAutoConnectRef.current = false;
      if (shouldRedirectToDepositOnFirstConnect(address)) {
        setActiveTab("deposit");
      }

      setProvider(customProvider);
      setSigner(customSigner);
      setActiveEip1193(eip1193Provider);

      // Detect chain and pick the correct contract.
      // Prefer the UI-selected config key when it matches the wallet EVM id —
      // mainnet/testnet siblings (`arbitrum` vs `arbitrum_mainnet`,
      // `ethereum` vs `ethereum_mainnet`, `base` vs `base_mainnet`,
      // `bnb` vs `bnb_mainnet`) are separate configs and must never be
      // interchanged.
      const { chainId } = await customProvider.getNetwork();
      // Ensure chainId is a number (coerce to avoid string comparison issues)
      const chainIdNum = Number(chainId);
      setCurrentChainId(chainIdNum);

      const preferredKey = getSavedChainId();
      const preferredCfg = preferredKey ? CHAINS[preferredKey] : null;
      const resolved = resolveChainEntry(chainIdNum, { preferredKey });
      const chainCfg =
        preferredCfg &&
        preferredCfg.chainIdDecimal !== chainIdNum &&
        preferredCfg.contractAddress
          ? preferredCfg
          : resolved?.chain;
      const chainKey = chainCfg?.id || resolved?.key;

      // Build contract for this chain (if configured)
      const addr = chainCfg?.contractAddress || null;
      const newContract = addr
        ? new ethers.Contract(
            addr,
            CONTRACT_ABI,
            preferredCfg &&
              preferredCfg.chainIdDecimal !== chainIdNum &&
              preferredCfg.rpcUrl
              ? new ethers.providers.JsonRpcProvider(preferredCfg.rpcUrl)
              : customSigner,
          )
        : null;
      setContract(newContract);

      if (!addr) {
        console.warn(
          "No contract address configured for",
          chainCfg?.name || "this chain",
        );
        // showNotification(
        //     chainCfg?.name
        //         ? `Please add ${chainCfg.name} to your wallet first, then try connecting again.`
        //         : `Please add a supported network to your wallet or switch to a supported network.`,
        //     'warning'
        // );
      } else {
      }

      // Check if user has Stabletrust account
      if (newContract) {
        try {
          const coreTuple = await newContract.getAccountCore(address);
          const core = parseAccountCore(coreTuple);

          const active = core.exists && core.finalized;
          setHasActiveAccount(active);
          setAccountExists(core.exists);

          // Always set accountData to reflect current account state
          // This ensures pendingAction and other flags are always current
          setAccountData(core);
        } catch (error) {
          console.error("Error checking account status:", error);
          setHasActiveAccount(false);
        }
      }

      // Only show connection notification if this is a different account or first connection
      if (lastConnectedAccount !== address) {
        // showNotification(`Connected to account: ${formatAddress(address)}`, 'success');
        setLastConnectedAccount(address);
      }
    } catch (error) {
      console.error("Error setting up wallet connection:", error);
      showNotification("Failed to complete wallet connection.", "error");
    }
  };

  // Set the ref immediately after render (useLayoutEffect runs synchronously after DOM updates)
  // This ensures the ref is available for the restore effect
  useLayoutEffect(() => {
    setWalletConnectedWithProviderRef.current = setWalletConnectedWithProvider;
  });

  const setWalletConnected = async (address, origin = "privy") => {
    // Store in ref so it can be accessed by ensurePrivyConnection
    setWalletConnectedRef.current = setWalletConnected;

    // Ignore if Privy flow is active and this is not a Privy-origin event
    if (
      connectTargetRef.current === CONNECT_TARGETS.PRIVY &&
      origin !== "privy"
    ) {
      return;
    }

    // HARD GUARD: do nothing if user has cancelled/closed Privy and no explicit flow is active
    if (!allowConnectNow(origin)) {
      return;
    }

    try {
      // Check if this is the same account (avoid unnecessary reconnection)
      if (userAccount === address && provider && signer) {
        return;
      }

      // IMPORTANT: Clear all account-specific state when switching accounts
      // This prevents stale data from the previous account from persisting
      const previousAccount = userAccount;
      setAccountData(null);
      setHasActiveAccount(false);
      setAccountExists(false);
      setConfidentialBalance(null);
      setPendingBalance(null);
      setSecureClaimInFlightByKey({});
      setDerivedKeys(null); // Keys will be re-derived for the new account
      setKeyBannerVisible(false);

      setUserAccount(address);
      setActiveWalletOrigin(origin);
      persistLastWallet(origin);
      setWalletTabActive(); // Mark tab as active so wallet persists on refresh
      clearWalletDisconnectedFlag(); // Clear disconnected flag since user is now connected
      if (shouldRedirectToDepositOnFirstConnect(address)) {
        setActiveTab("deposit");
      }

      // Clear suppression on successful wallet connection
      suppressAutoConnectRef.current = false;

      // Setup ethers provider and signer. Only an already-wired provider is
      // used — there is no window.ethereum fallback, so a missing provider
      // disconnects rather than silently attaching some other wallet.
      const ethereumProvider = activeEip1193 || walletConnectProvider;

      if (!ethereumProvider && origin === "privy") {
        console.error(
          "No Privy provider available in setWalletConnected; disconnecting.",
        );
        await setWalletDisconnected();
        return;
      }

      // >>> BLOCK: Phantom EVM is not allowed (even if it sneaks in as activeEip1193)
      if (isPhantomProvider(ethereumProvider)) {
        console.warn(
          "[Guard] setWalletConnected called with Phantom provider - blocking",
        );
        showNotification(
          "Phantom (EVM) is not supported. Please sign in with Privy.",
          "error",
        );
        await setWalletDisconnected();
        return;
      }

      if (!ethereumProvider) {
        throw new Error("No provider available for wallet connection");
      }
      const newProvider = new ethers.providers.Web3Provider(
        ethereumProvider,
        "any",
      );
      const newSigner = newProvider.getSigner();
      setProvider(newProvider);
      setSigner(newSigner);

      // Update active EIP-1193 provider if we're using a new one
      if (!activeEip1193) {
        setActiveEip1193(ethereumProvider);
      }

      // Detect chain and pick the correct contract
      const { chainId } = await newProvider.getNetwork();
      // Ensure chainId is a number (coerce to avoid string comparison issues)
      const chainIdNum = Number(chainId);
      setCurrentChainId(chainIdNum); // Update current chain ID

      const preferredKey = getSavedChainId();
      const resolved = resolveChainEntry(chainIdNum, { preferredKey });
      const chainCfg = resolved?.chain;
      const chainKey = resolved?.key;


      // Build contract for this chain (if configured)
      const addr = chainCfg?.contractAddress || null;
      const newContract = addr
        ? new ethers.Contract(addr, CONTRACT_ABI, newSigner)
        : null;
      setContract(newContract);

      if (!addr) {
        console.warn(
          "No contract address configured for",
          chainCfg?.name || "this chain",
        );
        showNotification(
          chainCfg?.name
            ? `Please add ${chainCfg.name} to your wallet first, then try connecting again.`
            : `Please add a supported network to your wallet or switch to a supported network.`,
          "warning",
        );
      } else {
      }

      // Check if user has Stabletrust account (exists && finalized)
      // Only if contract is available
      if (newContract) {
        try {
          const coreTuple = await newContract.getAccountCore(address);
          const core = parseAccountCore(coreTuple);

          const active = core.exists && core.finalized;
          setHasActiveAccount(active);
          setAccountExists(core.exists);

          // Always set accountData to reflect current account state
          // This ensures pendingAction and other flags are always current
          setAccountData(core);
        } catch (error) {
          console.error("Error checking account status:", error);
          // Don't fail the whole connection if account check fails
          setHasActiveAccount(false);
        }
      } else {
        // No contract configured for this chain
        setHasActiveAccount(false);
        setAccountExists(false);
      }

      // Only show connection notification if this is a different account or first connection
      if (lastConnectedAccount !== address) {
        //  showNotification(`Connected to account: ${formatAddress(address)}`, 'success');
        setLastConnectedAccount(address);
      }
    } catch (error) {
      console.error("Error setting up wallet connection:", error);

      // Only show error for specific connection failures, not for temporary issues
      if (
        error.code === "ACTION_REJECTED" ||
        error.code === "UNSUPPORTED_OPERATION"
      ) {
        // User rejected the connection or operation not supported
        showNotification(
          "Wallet connection was rejected or not supported.",
          "error",
        );
      } else if (error.message && error.message.includes("User denied")) {
        // User denied the connection
        showNotification("Wallet connection was denied.", "error");
      } else {
        // For other errors (network issues, etc.), don't show error notification
        // as they might be temporary and the connection could still succeed
      }
    }
  };

  const setWalletDisconnected = async () => {
    // Store in ref so it can be accessed by ensurePrivyConnection
    setWalletDisconnectedRef.current = setWalletDisconnected;

    // 1) Unregister all known handlers BEFORE clearing refs
    try {
      for (const [prov] of providerHandlersRef.current.entries()) {
        unregisterHandlers(prov);
      }
      providerHandlersRef.current.clear?.();
    } catch (e) {
      console.error("Error unregistering handlers:", e);
    }

    // 2) Now clear state/refs
    setUserAccount(null);
    setProvider(null);
    setSigner(null);
    setContract(null);
    setHasActiveAccount(false);
    setAccountExists(false);
    setAccountData(null);
    setActiveEip1193(null);
    setActiveWalletOrigin(null);
    clearLastWallet();
    clearWalletTabActive(); // Clear session flag when disconnecting
    setWalletDisconnectedFlag(); // Set disconnected flag to prevent auto-reconnect on refresh
    setActiveTab("deposit");
    didAutoRouteTabRef.current = false;

    setKeyBannerVisible(false);
    setDerivedKeys(null);
    setNotifications([]);
    setLastConnectedAccount(null);
    setLastNotifiedChain(null);

    // If we’re disconnecting while Privy was active, remember that this was manual
    // so we don’t auto-reconnect the embedded wallet in the background.
    if (activeWalletOriginRef.current === CONNECT_TARGETS.PRIVY) {
      manualPrivyDisconnectRef.current = true;
    }

    // 🔒 Block any background / auto-connect after an explicit disconnect.
    // Explicit connect flows still work because they set connectTarget
    // and/or use `forceAllow`.
    suppressAutoConnectRef.current = true;

    // 3) Disconnect WC if present, clear SDK, clear key cache
    if (walletConnectProvider) {
      try {
        await disconnectWalletConnect();
      } catch (e) {
        console.error("Error disconnecting WalletConnect:", e);
      }
      setWalletConnectProvider(null);
    }

    try {
      if (secureKeyCache && typeof secureKeyCache.clearAll === "function") {
        secureKeyCache.clearAll();
      }
    } catch (_) {}
  };

  // Force the UI/app back to the fully disconnected state and keep auto-connect blocked
  const resetToDisconnected = async () => {
    try {
      await setWalletDisconnected(); // you already clean handlers, providers, state here
    } finally {
      suppressAutoConnectRef.current = true; // keep blocking any background attach
      updateConnectTarget(CONNECT_TARGETS.NONE); // no active flow
    }
  };

  /**
   * Sign out of Privy, then immediately start a fresh login.
   *
   * Privy's `login()` is a no-op while a session exists, and `connectWithPrivy`
   * skips it outright when `authenticated` is already true — so without an
   * explicit `logout()` there is no way to reach a different account. Plain
   * "Disconnect" keeps the session on purpose (reconnect stays instant); this is
   * the escape hatch for "sign in as someone else".
   */
  const switchPrivyAccount = async () => {
    if (privySwitchInFlightRef.current) return;
    privySwitchInFlightRef.current = true;
    setPrivySwitching(true);

    try {
      // Invalidate any in-flight Privy connect so it cannot re-wire the wallet we
      // are about to sign out of. The generation bump makes its waits exit quietly.
      invalidateInFlightPrivyConnect();

      await resetToDisconnected();
      // Block the background re-attach effects for the gap between logout and the
      // new login; connectWithPrivy clears this again when the user picks Privy.
      manualPrivyDisconnectRef.current = true;

      try {
        await logoutRef.current();
      } catch (error) {
        console.error("[App.js] Privy logout failed:", error);
        showNotification(withCleanError("Could not sign out", error), "error");
        return;
      }

      // Privy tears its session down asynchronously. Until `authenticated` has
      // actually flipped false, `login()` stays a no-op and would silently
      // re-resolve to the account we just signed out of — so wait for it.
      const deadline = Date.now() + 8000;
      while (authenticatedRef.current && Date.now() < deadline) {
        await sleep(100);
      }
      if (authenticatedRef.current) {
        showNotification(
          "Signing out is taking longer than expected. Please refresh the page and try again.",
          "error",
        );
        return;
      }

      // Give Privy's client a beat to finish session teardown, then open a fresh
      // login via the latest connect handler (ref) so we don't call a stale
      // `login()` closed over the pre-logout authenticated session.
      await sleep(150);
      const selectWallet = handleWalletSelectRef.current;
      if (!selectWallet) {
        showNotification(
          "Could not start sign-in. Click Connect to choose an account.",
          "error",
        );
        return;
      }
      await selectWallet(CONNECT_TARGETS.PRIVY);
    } finally {
      privySwitchInFlightRef.current = false;
      setPrivySwitching(false);
    }
  };

  const refreshAccountStatus = async () => {

    // For Privy wallets, ensure connection is maintained after operations like account creation
    const shouldTryPrivyReconnect = !manualPrivyDisconnectRef.current;
    const isPrivyConnected = shouldTryPrivyReconnect
      ? await ensurePrivyConnection()
      : false;

    if (userAccount && contract) {
      await checkConfidentialAccount();
    } else {
      // Do not auto-connect to any cached/default wallet.
      // Respect user's explicit wallet choice from the selection modal.
    }
  };

  const fetchAccountCoreData = useCallback(
    async (addr, providerOverride, contractAddressOverride) => {
      const contractAddress =
        contractAddressOverride || contract?.address;
      if (!contractAddress) {
        return null;
      }

      // Guard: providerOverride must be a valid provider
      if (
        providerOverride &&
        !providerOverride.request &&
        !providerOverride.getNetwork
      ) {
        console.warn("Invalid providerOverride in fetchAccountCoreData");
        return null;
      }

      const reader = providerOverride
        ? new ethers.Contract(contractAddress, CONTRACT_ABI, providerOverride)
        : contract;

      if (!reader) {
        return null;
      }

      try {
        return await reader.getAccountCore(addr);
      } catch (e) {
        return null;
      }
    },
    [contract],
  );

  const checkConfidentialAccount = useCallback(async () => {
    if (!userAccount) return;

    // Store in ref so it can be accessed by ensurePrivyConnection
    checkConfidentialAccountRef.current = checkConfidentialAccount;

    const preferredKey = getSavedChainId();
    const preferredCfg = preferredKey ? CHAINS[preferredKey] : null;
    const selectedAddress = preferredCfg?.contractAddress || contract?.address;
    const selectedRpc =
      preferredCfg?.rpcUrl ||
      resolveRpcUrl(preferredCfg?.chainIdDecimal || currentChainId);

    if (!selectedAddress) {
      setHasActiveAccount(false);
      setAccountExists(false);
      setAccountData(null);
      setPendingActionKind(null);
      return;
    }

    try {
      // Always read exists/finalized against the UI-selected chain's
      // contract + RPC. Wallet/hybrid state can lag on mainnet after a
      // Testnet toggle, which previously made a real Sepolia account look
      // missing and opened "Create Your Arbitrum Sepolia Account".
      let coreTuple = null;
      if (selectedRpc) {
        const selectedProvider = new ethers.providers.JsonRpcProvider(
          selectedRpc,
        );
        coreTuple = await fetchAccountCoreData(
          userAccount,
          selectedProvider,
          selectedAddress,
        );
      }
      if (!coreTuple) {
        coreTuple = await fetchAccountCoreData(userAccount);
      }
      if (!coreTuple) {
        setHasActiveAccount(false);
        setAccountExists(false);
        setAccountData(null);
        setPendingActionKind(null);
        return;
      }

      const formattedAccountData = parseAccountCore(coreTuple);
      const hasAccount = formattedAccountData.exists && formattedAccountData.finalized;
      setHasActiveAccount(hasAccount);
      setAccountExists(!!formattedAccountData.exists);
      setAccountData(formattedAccountData);

      if (formattedAccountData.pendingAction) {
        try {
          const pendingReader = selectedRpc
            ? new ethers.Contract(
                selectedAddress,
                CONTRACT_ABI,
                new ethers.providers.JsonRpcProvider(selectedRpc),
              )
            : contract;
          const pendingActionTuple =
            await pendingReader.getPendingAction(userAccount);
          setPendingActionKind(parsePendingKind(pendingActionTuple[0]));
        } catch (error) {
          console.error("Error fetching pending action kind:", error);
          setPendingActionKind(null);
        }
      } else {
        setPendingActionKind(null);
      }
    } catch (error) {
      console.error("Error checking Stabletrust account:", error);
      setHasActiveAccount(false);
      setAccountExists(false);
      setAccountData(null);
      setPendingActionKind(null);
    }
  }, [contract, userAccount, currentChainId, fetchAccountCoreData]);

  useEffect(() => {
    checkConfidentialAccountRef.current = checkConfidentialAccount;
  }, [checkConfidentialAccount]);

  // Keep userAccountRef in sync with userAccount state
  useEffect(() => {
    userAccountRef.current = userAccount;
  }, [userAccount]);

  // Keep userAccount in sync with whatever the active provider thinks the account is.
  // This is a safety net in case some wallets don't fire accountsChanged reliably.
  useEffect(() => {
    if (!provider || !signer || !activeWalletOrigin) {
      return;
    }

    let cancelled = false;
    let intervalId = null;

    const pollAccounts = async () => {
      if (cancelled) return;
      try {
        const p = providerRef.current;
        if (!p) return;

        const accounts = await p.listAccounts();
        const next = accounts?.[0];
        const current = userAccountRef.current;

        if (next && current && next.toLowerCase() !== current.toLowerCase()) {

          const origin =
            activeWalletOriginRef.current || CONNECT_TARGETS.PRIVY;
          const s = signerRef.current;

          if (setWalletConnectedWithProviderRef.current && p && s) {
            await setWalletConnectedWithProviderRef.current(
              next,
              p,
              s,
              origin,
              { forceAllow: true },
            );
          } else if (setWalletConnectedRef.current) {
            await setWalletConnectedRef.current(next, origin);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[AccountPoll] listAccounts failed:", err);
        }
      }
    };

    // immediate check + periodic
    pollAccounts();
    intervalId = setInterval(pollAccounts, 1500);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [provider, signer, activeWalletOrigin]);

  useEffect(() => {
    if (!contract || !userAccount) {
      return;
    }
    checkConfidentialAccount();
  }, [contract, userAccount, currentChainId, checkConfidentialAccount]);

  // Open on Deposit for first-time / zero-balance wallets; Transfer once funded.
  // Runs once per connected address after available balance is known.
  useEffect(() => {
    didAutoRouteTabRef.current = false;
  }, [userAccount]);

  useEffect(() => {
    if (!userAccount) return;
    if (didAutoRouteTabRef.current) return;
    // null/undefined means the balance is still loading, and a fleeting 0 can
    // appear before the keypair derives / the balance decrypts.
    if (confidentialBalance === null || confidentialBalance === undefined) {
      return;
    }
    if (isZeroish(confidentialBalance)) return;
    didAutoRouteTabRef.current = true;
    setActiveTab("transfer");
  }, [userAccount, confidentialBalance]);

  // Reset pending balance banner dismissed state when a new pending balance appears or increases
  // This allows the banner to reappear if a new pending balance comes in or increases
  useEffect(() => {
    const prev = prevPendingBalanceRef.current;
    const current = pendingBalance;

    // Only reset dismissed state if:
    // 1. Previous was null/zero and current is non-zero (new balance appeared)
    // 2. Current is greater than previous (balance increased)
    if (current != null && !isZeroish(current)) {
      if (prev == null || isZeroish(prev) || current > prev) {
        setPendingBalanceBannerDismissed(false);
      }
    }

    prevPendingBalanceRef.current = current;
  }, [pendingBalance]);

  // Restore the last connected wallet after a hard refresh
  useEffect(() => {
    // CRITICAL: Check if user explicitly disconnected - if so, don't auto-reconnect
    if (isWalletDisconnected()) {
      clearLastWallet(); // Clear localStorage since we're not restoring
      return;
    }

    // Read the wallet origin FIRST and set connectTarget IMMEDIATELY, so the
    // restore below is recognised as an explicit flow by `allowConnectNow`.
    const origin = readLastWallet();

    if (origin === CONNECT_TARGETS.PRIVY) {
      connectTargetRef.current = CONNECT_TARGETS.PRIVY;
      updateConnectTarget(CONNECT_TARGETS.PRIVY);
      manualPrivyDisconnectRef.current = false;
    }

    // Don't restore if we're already connected
    if (userAccount && provider && signer) {
      // Make sure session flag is set if already connected
      if (userAccount) {
        setWalletTabActive();
      }
      return;
    }

    // Check if tab was closed (sessionStorage cleared) - don't restore if tab was closed
    const tabActive = isWalletTabActive();
    if (!tabActive) {
      clearLastWallet(); // Clear localStorage as well since we're not restoring
      return;
    }

    if (!origin) {
      return;
    }


    // Privy: we already have a dedicated auto-connect flow (ensurePrivyConnection).
    // connectTarget was already set above, so just return here.
    if (origin === CONNECT_TARGETS.PRIVY) {
      return;
    }

    const restore = async () => {
      try {
        // Wait for the function to be available (should be set by useLayoutEffect, but wait just in case)
        let retries = 10; // Wait up to 1 second (10 * 100ms) - should be immediate due to useLayoutEffect
        while (!setWalletConnectedWithProviderRef.current && retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          retries--;
        }

        if (!setWalletConnectedWithProviderRef.current) {
          console.error(
            "[WalletPersist] ✗ setWalletConnectedWithProvider not available after waiting",
          );
          console.error(
            "[WalletPersist] This should not happen - useLayoutEffect should have set it",
          );
          return;
        }

        // Set connectTarget to help the restore (even though forceAllow should bypass)
        if (origin === CONNECT_TARGETS.COINBASE) {
          connectTargetRef.current = CONNECT_TARGETS.COINBASE;
          updateConnectTarget(CONNECT_TARGETS.COINBASE);
        }

        let eip1193 = null;

        if (origin === CONNECT_TARGETS.COINBASE) {
          try {
            await discoverEip6963Providers(800);
          } catch (e) {
            console.warn("[WalletPersist] EIP-6963 discovery failed:", e);
          }
          eip1193 = getCoinbaseWalletProvider();
          if (!eip1193) {
            console.warn("[WalletPersist] Coinbase provider not found");
            return;
          }
        } else {
          // Trust / OKX / other wallets that you connected via WalletConnect
          // (getWalletConnectProvider should re-use the existing WC session)
          eip1193 = getWalletConnectProvider();
          if (!eip1193) {
            console.warn("[WalletPersist] WalletConnect provider not found");
            return;
          }
        }

        if (!eip1193 || !eip1193.request) {
          console.warn("[WalletPersist] Provider missing request method");
          return;
        }

        // Only attach if the wallet still has an authorised account for this site
        const accounts = await eip1193.request({ method: "eth_accounts" });
        if (!accounts || accounts.length === 0) {
          console.warn(
            "[WalletPersist] ✗ No authorized accounts found - user disconnected at wallet level",
          );
          console.warn(
            "[WalletPersist] The wallet extension is not connected to this site. User needs to reconnect manually.",
          );
          return; // user is no longer "connected" at the wallet level
        }

        const addr = accounts[0];

        // Rebuild ethers provider/signer around that EIP-1193 provider
        const ethersProvider = new ethers.providers.Web3Provider(
          eip1193,
          "any",
        );
        const signer = ethersProvider.getSigner();

        // This is a user-initiated session being restored,
        // so we explicitly bypass auto-connect guards here.
        suppressAutoConnectRef.current = false;
        setActiveEip1193(eip1193);


        const result = await setWalletConnectedWithProviderRef.current(
          addr,
          ethersProvider,
          signer,
          origin,
          { forceAllow: true }, // <- ignore allowConnectNow/suppression
        );

      } catch (err) {
        console.error("[WalletPersist] Silent restore failed:", err);
        console.error("[WalletPersist] Error details:", {
          message: err.message,
          stack: err.stack,
          origin: origin,
        });
      }
    };

    // Delay to ensure component is fully mounted and all functions are defined
    // We need to wait for setWalletConnectedWithProvider to be defined, which happens
    // when the component renders and the function is created
    const timeoutId = setTimeout(() => {
      restore();
    }, 500); // Increased delay to ensure functions are defined

    return () => clearTimeout(timeoutId);
    // We intentionally only want this to run once on mount (empty deps array is correct)
  }, []);

  const showNotification = useCallback(
    (message, type = "info", persistent = false) => {
      pushNotification(setNotifications, message, type, persistent);
    },
    [setNotifications],
  );

  const hasAccountPendingAction = !!(accountData && accountData.pendingAction);

  useEffect(() => {
    if (activeWalletOrigin !== CONNECT_TARGETS.PRIVY) {
      return;
    }
    if (!contract || !userAccount) {
      return;
    }

    const hasPending = !hasActiveAccount || hasAccountPendingAction;
    if (!hasPending) {
      return;
    }

    let intervalId = null;

    const poll = async () => {
      try {
        await checkConfidentialAccount();
      } catch (err) {
        console.error("Privy poll error:", err);
      }
    };

    poll();
    intervalId = setInterval(poll, 4000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    activeWalletOrigin,
    contract,
    userAccount,
    hasActiveAccount,
    hasAccountPendingAction,
    checkConfidentialAccount,
  ]);

  // Connect with Privy: login + wire whichever wallet Privy resolves (the
  // embedded one, or the external wallet the user signed in with) + keys.
  const connectWithPrivy = async () => {
    // A second entry while hookup is running used to return silently and leave
    // the Connect Wallet modal looking dead. Callers invalidate first; if flags
    // are still set, clear them and continue with a fresh generation.
    if (
      privyLoginModalInFlightRef.current ||
      privyWalletHookupInFlightRef.current
    ) {
      invalidateInFlightPrivyConnect();
    }

    // Guard against other connect flows
    if (connectTarget && connectTarget !== CONNECT_TARGETS.PRIVY) {
      return;
    }

    let myGen = 0;
    try {
      myGen = ++privyConnectGenerationRef.current;
      const isPrivyConnectStale = () =>
        privyConnectGenerationRef.current !== myGen;

      // We're starting a fresh explicit Privy flow: allow auto-maintenance again
      manualPrivyDisconnectRef.current = false;

      // Start clean: fully disconnected, no active provider
      await resetToDisconnected();
      if (isPrivyConnectStale()) return;

      // CRITICAL: Clear the disconnect flag AFTER resetToDisconnected()
      // because resetToDisconnected() calls setWalletDisconnected() which sets the flag
      // We need to clear it so auto-connect can work (especially important for first-time signups)
      clearWalletDisconnectedFlag();

      // Set tab active flag early for first-time logins
      // This ensures that if something fails later and we need to retry via ensurePrivyConnection,
      // it won't be blocked by the isWalletTabActive() check
      setWalletTabActive();

      updateConnectTarget(CONNECT_TARGETS.PRIVY);

      const waitForCondition = async (
        predicate,
        timeoutMs = 10000,
        intervalMs = 200,
      ) => {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
          if (isPrivyConnectStale()) return false;
          if (predicate()) {
            return true;
          }
          await sleep(intervalMs);
        }
        if (isPrivyConnectStale()) return false;
        return predicate();
      };

      // 🔁 0) WAIT for Privy SDK to be ready instead of returning early
      if (!privyReadyRef.current) {
        showNotification("Initializing Privy…", "info");
      }
      const readyOk = await waitForCondition(
        () => privyReadyRef.current,
        15000,
        250,
      );
      if (!readyOk) {
        showNotification(
          "Privy is still starting. Refresh and try again.",
          "error",
        );
        return;
      }

      // 1) Ensure a Privy session — and when an external-wallet account's
      //    extension is gone (MetaMask logged out / disconnected), do NOT try
      //    to force a MetaMask reconnect. Sign out and open the full login
      //    modal (email / Google / wallets) instead; `login()` is a no-op while
      //    still authenticated.
      const waitUntilPrivySessionVisible = async (timeoutMs = 12000) => {
        const deadline = Date.now() + timeoutMs;
        await sleep(100);
        while (Date.now() < deadline) {
          if (isPrivyConnectStale()) return false;
          if (authenticatedRef.current) return true;
          try {
            const tok = await getAccessTokenRef.current();
            if (tok) return true;
          } catch {
            /* session not ready yet */
          }
          await sleep(200);
        }
        return authenticatedRef.current;
      };

      const runPrivyLoginModal = async () => {
        privyLoginModalInFlightRef.current = true;
        try {
          try {
            // Always use the latest hook fn — a stale `login` from before a
            // switch-account logout can no-op and leave the UI disconnected.
            await loginRef.current();
          } catch (err) {
            console.error("Privy login error:", err);
            if (err?.code !== 4001) {
              showNotification(
                withCleanError("Privy login failed", err),
                "error",
              );
            }
            return false;
          }
        } finally {
          // Critical: release as soon as the Privy UI promise settles (including X on passkey chooser)
          // so Connect is not blocked while we poll for session / wire wallets below.
          privyLoginModalInFlightRef.current = false;
          // handleWalletSelect holds this true for the whole connect; without clearing it here,
          // "Connect" stays ignored until session polling finishes (~12s) after the user dismisses (X).
          isConnectingToWalletRef.current = false;
        }

        const loggedIn = await waitUntilPrivySessionVisible();
        if (isPrivyConnectStale()) return false;
        if (!loggedIn) {
          // Dismissed / abandoned login do not imply a hard failure
          return false;
        }
        await sleep(500);
        return !isPrivyConnectStale();
      };

      let needsLoginModal = !authenticatedRef.current;

      // Session survived Disconnect, but the extension may not be in useWallets
      // anymore. Prefer a fresh login modal over forcing MetaMask reconnect.
      if (
        !needsLoginModal &&
        signedUpWithExternalWallet(privyUserRef.current)
      ) {
        await waitForCondition(
          () => walletsReadyRef.current === true,
          5000,
          200,
        );
        if (isPrivyConnectStale()) return;
        // Brief grace for Privy to hydrate the linked extension — no wallet UI.
        const externalReady = await waitForCondition(
          () =>
            !!resolvePrivyWallet(
              privyWalletsRef.current,
              privyUserRef.current,
            ),
          2000,
          250,
        );
        if (isPrivyConnectStale()) return;
        if (!externalReady) {
          needsLoginModal = true;
          try {
            await logoutRef.current();
          } catch (logoutErr) {
            console.error(
              "[Privy] logout before fresh login failed:",
              logoutErr,
            );
          }
          const logoutDeadline = Date.now() + 8000;
          while (authenticatedRef.current && Date.now() < logoutDeadline) {
            await sleep(100);
          }
          if (isPrivyConnectStale()) return;
          if (authenticatedRef.current) {
            showNotification(
              "Could not reset sign-in. Use a different account, then try again.",
              "error",
            );
            return;
          }
        }
      }

      if (needsLoginModal) {
        const ok = await runPrivyLoginModal();
        if (!ok) return;
      }

      privyWalletHookupInFlightRef.current = true;

      // 2) Wait for wallets list to load (give extra time after login for wallet creation)
      // After first-time login, Privy may need to create the embedded wallet, so we wait longer
      const walletsReadyOk = await waitForCondition(
        () => {
          const ready = walletsReadyRef.current;
          if (
            ready &&
            privyWalletsRef.current &&
            privyWalletsRef.current.length > 0
          ) {
            // Wallets are ready and we have at least one wallet
            return true;
          }
          return ready; // At least wallets are ready, even if list is empty
        },
        25000, // Increased timeout to allow wallet creation after first login
        400, // Increased interval for faster detection
      );
      if (isPrivyConnectStale()) return;
      if (!walletsReadyOk) {
        showNotification(
          "Privy wallets did not load. Refresh and try again.",
          "error",
        );
        return;
      }

      // After login, give Privy a moment to create the embedded wallet if it doesn't exist yet
      // Refresh the wallets list to get the latest state
      if (!privyWalletsRef.current || privyWalletsRef.current.length === 0) {
        // Wait a bit longer for Privy to create the embedded wallet after first-time login
        const walletAppeared = await waitForCondition(
          () => privyWalletsRef.current && privyWalletsRef.current.length > 0,
          10000, // Wait up to 10 seconds for wallet to appear
          500, // Check every 500ms
        );
        if (walletAppeared) {
        } else {
        }
      }

      let wallet = resolvePrivyWallet(
        privyWalletsRef.current,
        privyUserRef.current,
      );

      // The list hydrates asynchronously and an injected extension can show up
      // before the embedded wallet does. When the account owns an embedded
      // wallet, wait for it rather than connecting whatever arrived first —
      // the user's confidential account lives at the embedded address.
      if (
        wallet &&
        !isEmbeddedPrivyWallet(wallet) &&
        shouldHoldOutForEmbeddedWallet(privyUserRef.current)
      ) {
        const embeddedArrived = await waitForCondition(
          () => {
            const embedded = (privyWalletsRef.current || []).find(
              isEmbeddedPrivyWallet,
            );
            if (embedded) {
              wallet = embedded;
              return true;
            }
            return false;
          },
          15000,
          400,
        );
        if (isPrivyConnectStale()) return;
        if (!embeddedArrived) {
          // Stop rather than connect a different address: this account's
          // balances and keys live on the embedded wallet, so falling back to
          // the extension would look like an empty, unrelated account.
          showNotification(
            "Your Privy wallet did not load. Refresh and try again.",
            "error",
          );
          return;
        }
      }

      // Privy can report the wallet a moment before it is marked as linked to
      // the freshly authenticated user. Give that a beat rather than treating a
      // wallet login as "no wallet" and provisioning an embedded one instead.
      if (!wallet && (privyWalletsRef.current || []).length > 0) {
        await waitForCondition(
          () => {
            const linkedWallet = resolvePrivyWallet(
              privyWalletsRef.current,
              privyUserRef.current,
            );
            if (linkedWallet) {
              wallet = linkedWallet;
              return true;
            }
            return false;
          },
          5000,
          300,
        );
        if (isPrivyConnectStale()) return;
      }

      // 3) Nothing usable in the list at all — this is an email/social/passkey
      //    user whose embedded wallet has not been provisioned yet, so make one.
      //    Users who logged in with an external wallet already have an entry
      //    here and never reach this branch.
      // Signed up with an external wallet that still isn't answering after the
      // fresh-login path above: never provision an embedded wallet in its
      // place — that would move this user to a new, empty address for good.
      if (!wallet && signedUpWithExternalWallet(privyUserRef.current)) {
        showNotification(
          "No wallet connected. Choose email, Google, or a wallet in the sign-in options.",
          "error",
        );
        return;
      }

      if (!wallet) {
        if (createWalletRef.current) {
          try {
            showNotification("Creating your embedded Privy wallet...", "info");
            await createWalletRef.current();
          } catch (createErr) {
            console.error("Failed to create embedded Privy wallet:", createErr);
            const alreadyHasEmbedded =
              typeof createErr?.message === "string" &&
              /already has an embedded wallet/i.test(createErr.message);

            if (!alreadyHasEmbedded) {
              showNotification(
                withCleanError("Could not create embedded Privy wallet", createErr),
                "error",
              );
              return;
            }

            showNotification(
              "Detected existing embedded Privy wallet. Loading it now...",
              "info",
            );
          }
        } else {
          console.warn("[Privy] createWallet function not available");
          showNotification(
            "Could not create Privy wallet. Try again.",
            "error",
          );
          return;
        }

        // Wait for the embedded wallet to appear after creation
        const embeddedReady = await waitForCondition(
          () => {
            const embedded = resolvePrivyWallet(
              privyWalletsRef.current,
              privyUserRef.current,
            );
            if (embedded) {
              wallet = embedded;
              return true;
            }
            return false;
          },
          25000,
          400,
        ); // Increased timeout for first-time wallet creation

        if (!embeddedReady || !wallet) {
          showNotification(
            "No embedded Privy wallet found. Please check your Privy configuration.",
            "error",
          );
          return;
        }
      }

      // Confirm the extension that answered is the one the user picked.
      // Privy's fallback provider sniffing is spoofable, so a wallet that is
      // not installed can be answered for by whichever extension owns
      // window.ethereum — see verifyInjectedWalletIdentity.
      if (
        !isEmbeddedPrivyWallet(wallet) &&
        wallet.connectorType === "injected"
      ) {
        const { ok, detected } = await verifyInjectedWalletIdentity(
          wallet.walletClientType,
        );
        if (isPrivyConnectStale()) return;
        if (!ok) {
          const requested = describePrivyWallet(wallet);
          console.warn("[Privy] Injected wallet mismatch", {
            requested: wallet.walletClientType,
            detected,
          });
          showNotification(
            `${requested} is not available in this browser — ${detected.join(", ")} answered instead. ` +
              `Install or enable ${requested} (or disable the other extension) and try again.`,
            "error",
          );
          // The SIWE login already succeeded against the impostor's address, so
          // end that session: leaving it would let the auto-connect effects wire
          // the wrong wallet the moment this flow returns.
          manualPrivyDisconnectRef.current = true;
          try {
            await logoutRef.current?.();
          } catch (logoutErr) {
            console.error("[Privy] logout after wallet mismatch failed:", logoutErr);
          }
          await resetToDisconnected();
          return;
        }
      }

      // 4) Ensure we're on the selected chain (default Arbitrum mainnet).
      //    Embedded wallets switch silently; an external wallet shows its own
      //    prompt here (and adds the network if it doesn't have it yet).
      const selected = getSavedChainId() || "arbitrum_mainnet";
      const targetDec = parseInt(NETWORK_CONFIGS[selected].chainId, 16);
      try {
        await wallet.switchChain(targetDec);
      } catch (switchError) {
        // Handle chain switching errors, especially for unsupported chains
        if (
          switchError?.message?.includes("Unsupported chainId") ||
          switchError?.message?.includes("unsupported chain")
        ) {
          const chainName = NETWORK_CONFIGS[selected]?.chainName || selected;
          showNotification(
            `Privy does not support ${chainName} (chainId: ${targetDec}). Please use a different wallet or chain.`,
            "error",
          );
          throw switchError;
        }
        // An external wallet can have the network switch declined in the
        // extension. Say so plainly instead of surfacing the raw provider error.
        const switchMessage = switchError?.message?.toLowerCase?.() || "";
        if (
          switchError?.code === 4001 ||
          switchMessage.includes("rejected") ||
          switchMessage.includes("denied")
        ) {
          const chainName = NETWORK_CONFIGS[selected]?.chainName || selected;
          showNotification(
            `${describePrivyWallet(wallet)} stayed on its current network. Approve the switch to ${chainName} to continue.`,
            "warning",
          );
        }
        throw switchError;
      }

      // 5) Get EIP-1193 provider and wire it into ethers
      const rawEip1193 = await wallet.getEthereumProvider();
      const eip1193 = makeHybridEip1193(rawEip1193, targetDec);
      const provider = new ethers.providers.Web3Provider(eip1193, "any");
      const signer = provider.getSigner();
      const accounts = await provider.listAccounts();
      if (!accounts?.length) {
        throw new Error("No accounts returned by Privy provider.");
      }

      const account = accounts[0];
      setActiveEip1193(eip1193);
      suppressAutoConnectRef.current = false; // explicit connection, allow events

      await setWalletConnectedWithProvider(
        account,
        provider,
        signer,
        "privy",
        { forceAllow: true }, // bypass auto-connect suppression guards
      );

      // 6) Listen for account/chain changes on the Privy provider
      registerHandlers(
        eip1193,
        {
          accountsChanged: (accs) => {
            if (accs.length === 0) {
              setWalletDisconnected();
              return;
            }
            const currentProvider = provider;
            const currentSigner = signer;
            if (currentProvider && currentSigner) {
              setWalletConnectedWithProvider(
                accs[0],
                currentProvider,
                currentSigner,
                "privy",
              );
            } else {
              setWalletConnected(accs[0], "privy");
            }
          },
          chainChanged: (chainId) => {
            const newChainId = parseInt(chainId, 16);
            setCurrentChainId(newChainId);
            setTimeout(checkConfidentialAccount, 1000);
          },
        },
        "privy",
      );

      // 7) Run Stabletrust account check (drives key-derive flow)
      setTimeout(checkConfidentialAccount, 500);
      showNotification(`Connected with ${describePrivyWallet(wallet)}`, "success");
    } catch (err) {
      if (privyConnectGenerationRef.current !== myGen) {
        return;
      }
      console.error("Privy connect error:", err);
      await setWalletDisconnected();
      suppressAutoConnectRef.current = true;
      if (err?.code !== 4001) {
        // Check if error is due to unsupported chain
        const errorMessage = err?.message || String(err);
        if (
          errorMessage.includes("Unsupported chainId") ||
          errorMessage.includes("unsupported chain")
        ) {
          showNotification(
            "This chain is not supported by Privy. Switch to a supported chain or use a different wallet.",
            "error",
          );
        } else {
          showNotification(withCleanError("Privy connect failed", err), "error");
        }
      }
    } finally {
      // A newer explicit connect may be running; do not tear down its flags / connect target.
      if (privyConnectGenerationRef.current !== myGen) {
        return;
      }
      privyWalletHookupInFlightRef.current = false;
      privyLoginModalInFlightRef.current = false;
      updateConnectTarget((prev) =>
        prev === CONNECT_TARGETS.PRIVY ? CONNECT_TARGETS.NONE : prev,
      );
      // If the user cancelled before a successful wire-up, allow another explicit connect
      // without leaving auto-connect suppressed (resetToDisconnected sets this true).
      if (!userAccountRef.current) {
        suppressAutoConnectRef.current = false;
      }
    }
  };

  // Open wallet selection modal (used by connect buttons throughout the app)
  const openWalletSelectionModal = async () => {
    // Cancel a stuck/hanging Privy reconnect (common after MetaMask logout) so
    // "Continue with Privy" is not a silent no-op while the previous attempt waits.
    invalidateInFlightPrivyConnect();

    // While picker is open, stop any auto-connect races
    suppressAutoConnectRef.current = true;

    setAvailableWalletsForModal(null); // Show loading state
    setShowWalletSelectionModal(true);
    // Load available wallets
    const wallets = await getAvailableWallets();

    // Remove anything Phantom-looking (keys or metadata vary per env)
    const sanitized = Object.fromEntries(
      Object.entries(wallets || {}).filter(([k, v]) => {
        const id = (typeof v === "string" ? v : v?.id) || "";
        const name = (typeof v === "string" ? v : v?.name) || "";
        return (
          !/phantom/i.test(k) &&
          !/phantom/i.test(id) &&
          !/phantom/i.test(name)
        );
      }),
    );

    setAvailableWalletsForModal(sanitized);
  };

  // Handle wallet selection from modal
  const handleWalletSelect = async (walletId) => {
    const normalizedWalletIdEarly = String(walletId).trim();
    if (normalizedWalletIdEarly === "privy") {
      // A prior connect can still be waiting on MetaMask/Privy UI. Supersede it
      // instead of returning with the modal still open and looking broken.
      invalidateInFlightPrivyConnect();
    } else if (isConnectingToWalletRef.current) {
      return;
    }

    // Set flag to prevent auto-connection to other wallets
    isConnectingToWalletRef.current = true;

    // Close the modal first
    setShowWalletSelectionModal(false);

    // Normalize walletId (trim whitespace, ensure it's a string)
    const normalizedWalletId = String(walletId).trim();

    // Belt-and-suspenders: refuse explicit Phantom choices
    if (/phantom/i.test(normalizedWalletId)) {
      showNotification(
        "Phantom (EVM) is not supported. Please sign in with Privy.",
        "error",
      );
      isConnectingToWalletRef.current = false;
      return;
    }

    // Always start from a clean slate before switching wallets so old listeners/providers are removed
    try {
      await resetToDisconnected();
    } catch (resetErr) {
      console.error("[App.js] Failed to reset before wallet switch:", resetErr);
      showNotification(
        "Could not reset the previous wallet connection. Try again.",
        "error",
      );
      isConnectingToWalletRef.current = false;
      return;
    }

    // CRITICAL: Early return for Privy - must be checked FIRST before any other wallet logic
    if (normalizedWalletId === "privy") {
      suppressAutoConnectRef.current = true; // Block other wallets' auto-connect
      updateConnectTarget(CONNECT_TARGETS.PRIVY);

      const uiAttempt = ++privyConnectUiAttemptRef.current;
      try {
        await connectWithPrivy(); // Will set userAccount on success
      } catch (error) {
        console.error("[App.js] Privy connection error:", error);
      } finally {
        if (uiAttempt === privyConnectUiAttemptRef.current) {
          updateConnectTarget((prev) =>
            prev === CONNECT_TARGETS.PRIVY ? CONNECT_TARGETS.NONE : prev,
          );
          isConnectingToWalletRef.current = false;
        }
      }
      return; // IMPORTANT: Return early to prevent any other wallet logic from running
    }


    const isMobile = isMobileDevice();

    // For now, mobile is not fully supported - show message
    if (isMobile) {
      showNotification(
        "Wallet connection is currently only available on desktop. Please use a desktop browser.",
        "warning",
      );
      return;
    }

    // Get available wallets to check for desktop extensions
    const availableWallets = await getAvailableWallets();

    if (normalizedWalletId === "coinbase") {
      updateConnectTarget(CONNECT_TARGETS.COINBASE);
      try {
        if (!availableWallets?.coinbase) {
          showNotification(
            "Coinbase Wallet extension is not installed. Please install the Coinbase Wallet browser extension.",
            "error",
          );
          isConnectingToWalletRef.current = false;
          return;
        }

        await discoverEip6963Providers(1200).catch(() => {});
        const coinbaseProvider = getCoinbaseWalletProvider();
        if (!coinbaseProvider) {
          showNotification(
            "Coinbase Wallet extension is not detected. Please ensure it is installed and enabled.",
            "error",
          );
          isConnectingToWalletRef.current = false;
          return;
        }

        const connectionResult = await connectWithCoinbaseWallet();

        // For Coinbase Wallet, ensure we properly extract the underlying EIP-1193 provider
        // The provider.provider should give us the raw EIP-1193 provider
        let eip1193 = null;
        if (connectionResult.provider && connectionResult.provider.provider) {
          eip1193 = connectionResult.provider.provider;
        } else if (connectionResult.provider) {
          // Fallback: try to get the underlying provider
          eip1193 = connectionResult.provider;
        }

        // If we still don't have the EIP-1193 provider, get it from Coinbase Wallet directly
        if (!eip1193) {
          const coinbaseProvider = getCoinbaseWalletProvider();
          if (coinbaseProvider) {
            eip1193 = coinbaseProvider;
          }
        }

        if (
          !connectionResult.accounts ||
          connectionResult.accounts.length === 0
        ) {
          showNotification(
            "No accounts returned from Coinbase Wallet",
            "error",
          );
          isConnectingToWalletRef.current = false;
          return;
        }

        // Ensure the EIP-1193 provider is set before connecting
        if (eip1193) {
          setActiveEip1193(eip1193);
        }

        await setWalletConnectedWithProvider(
          connectionResult.accounts[0],
          connectionResult.provider,
          connectionResult.signer,
          "coinbase",
        );

        registerHandlers(
          eip1193,
          {
            accountsChanged: (accs) => {
              if (accs.length === 0) {
                setWalletDisconnected();
                return;
              }
              const currentProvider = provider;
              const currentSigner = signer;
              if (currentProvider && currentSigner) {
                setWalletConnectedWithProvider(
                  accs[0],
                  currentProvider,
                  currentSigner,
                  "coinbase",
                );
              } else {
                setWalletConnected(accs[0], "coinbase");
              }
            },
            chainChanged: (chainId) => {
              const newChainId = parseInt(chainId, 16);
              setCurrentChainId(newChainId);
              setTimeout(checkConfidentialAccount, 1000);
            },
          },
          "coinbase",
        );

        const preferred = getSavedChainId();
        if (preferred && NETWORK_CONFIGS[preferred]) {
          try {
            const currentNetwork = await connectionResult.provider.getNetwork();
            const preferredConfig = NETWORK_CONFIGS[preferred];
            const preferredChainId = parseInt(preferredConfig.chainId, 16);
            const currentChainIdNum = Number(currentNetwork.chainId);

            if (currentChainIdNum !== preferredChainId) {
              switchToChain(preferred).catch((err) => {
              });
            }
          } catch (err) {
          }
        }

        suppressAutoConnectRef.current = false;
        isConnectingToWalletRef.current = false;
        setTimeout(checkConfidentialAccount, 500);
        return;
      } catch (e) {
        console.error("Coinbase Wallet connection error:", e);
        isConnectingToWalletRef.current = false;
        if (e.code === 4001) {
          // User cancelled - don't notify, they already know
        } else if (e.code === -32002) {
          // Request already pending - wallet already shows this
        } else {
          showNotification(
            withCleanError("Could not connect to Coinbase Wallet", e),
            "error",
          );
        }
        return;
      } finally {
        updateConnectTarget((prev) =>
          prev === CONNECT_TARGETS.COINBASE ? CONNECT_TARGETS.NONE : prev,
        );
      }
    }

    if (normalizedWalletId === "rainbow") {
      updateConnectTarget(CONNECT_TARGETS.RAINBOW);
      try {
        if (!availableWallets?.rainbow) {
          showNotification(
            "Rainbow Wallet extension is not installed. Please install the Rainbow Wallet browser extension.",
            "error",
          );
          isConnectingToWalletRef.current = false;
          return;
        }

        await discoverEip6963Providers(1200).catch(() => {});
        const rainbowProvider = getRainbowWalletProvider();
        if (!rainbowProvider) {
          showNotification(
            "Rainbow Wallet extension is not detected. Please ensure it is installed and enabled.",
            "error",
          );
          isConnectingToWalletRef.current = false;
          return;
        }

        const connectionResult = await connectWithRainbowWallet();

        // For Rainbow Wallet, ensure we properly extract the underlying EIP-1193 provider
        let eip1193 = null;
        if (connectionResult.provider && connectionResult.provider.provider) {
          eip1193 = connectionResult.provider.provider;
        } else if (connectionResult.provider) {
          eip1193 = connectionResult.provider;
        }

        if (!eip1193) {
          const rainbowProvider = getRainbowWalletProvider();
          if (rainbowProvider) {
            eip1193 = rainbowProvider;
          }
        }

        if (
          !connectionResult.accounts ||
          connectionResult.accounts.length === 0
        ) {
          showNotification("No accounts returned from Rainbow Wallet", "error");
          isConnectingToWalletRef.current = false;
          return;
        }

        if (eip1193) {
          setActiveEip1193(eip1193);
        }

        await setWalletConnectedWithProvider(
          connectionResult.accounts[0],
          connectionResult.provider,
          connectionResult.signer,
          "rainbow",
        );

        registerHandlers(
          eip1193,
          {
            accountsChanged: (accs) => {
              if (accs.length === 0) {
                setWalletDisconnected();
                return;
              }
              const currentProvider = provider;
              const currentSigner = signer;
              if (currentProvider && currentSigner) {
                setWalletConnectedWithProvider(
                  accs[0],
                  currentProvider,
                  currentSigner,
                  "rainbow",
                );
              } else {
                setWalletConnected(accs[0], "rainbow");
              }
            },
            chainChanged: (chainId) => {
              const newChainId = parseInt(chainId, 16);
              setCurrentChainId(newChainId);
              setTimeout(checkConfidentialAccount, 1000);
            },
          },
          "rainbow",
        );

        const preferred = getSavedChainId();
        if (preferred && NETWORK_CONFIGS[preferred]) {
          try {
            const currentNetwork = await connectionResult.provider.getNetwork();
            const preferredConfig = NETWORK_CONFIGS[preferred];
            const preferredChainId = parseInt(preferredConfig.chainId, 16);
            const currentChainIdNum = Number(currentNetwork.chainId);

            if (currentChainIdNum !== preferredChainId) {
              switchToChain(preferred).catch((err) => {
              });
            }
          } catch (err) {
          }
        }

        suppressAutoConnectRef.current = false;
        isConnectingToWalletRef.current = false;
        setTimeout(checkConfidentialAccount, 500);
        return;
      } catch (e) {
        console.error("Rainbow Wallet connection error:", e);
        isConnectingToWalletRef.current = false;
        if (e.code === 4001) {
          // User cancelled - don't notify, they already know
        } else if (e.code === -32002) {
          // Request already pending - wallet already shows this
        } else {
          showNotification(
            withCleanError("Could not connect to Rainbow Wallet", e),
            "error",
          );
        }
        return;
      } finally {
        updateConnectTarget((prev) =>
          prev === CONNECT_TARGETS.RAINBOW ? CONNECT_TARGETS.NONE : prev,
        );
      }
    }

    // For WalletConnect wallets (Trust Wallet, OKX Wallet)
    if (normalizedWalletId.startsWith("walletconnect-")) {
      const walletType = normalizedWalletId.replace("walletconnect-", "");
      const walletName = walletType
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      const origin =
        walletType === "trust" ? "trust" : walletType === "okx" ? "okx" : "okx";

      updateConnectTarget(
        origin === "trust" ? CONNECT_TARGETS.TRUST : CONNECT_TARGETS.OKX,
      );

      try {
        // First, try to connect via desktop extension if available
        let connectionResult;
        let eip1193 = null;

        if (walletType === "trust") {
          // STRICT: Only connect to Trust Wallet extension, NO WalletConnect fallback
          if (!availableWallets?.trustwallet) {
            showNotification(
              "Trust Wallet extension is not installed. Please install the Trust Wallet browser extension.",
              "error",
            );
            isConnectingToWalletRef.current = false;
            return;
          }
          try {
            // Removed "Connecting to..." notification - connection happens quickly
            connectionResult = await connectWithTrustWallet();
            eip1193 =
              connectionResult.provider.provider || connectionResult.provider;
          } catch (error) {
            // If extension connection fails, show error and return - DO NOT fallback to WalletConnect
            console.error("Trust Wallet extension connection error:", error);
            isConnectingToWalletRef.current = false;
            if (error.code === 4001) {
              // User cancelled - don't notify, they already know
            } else {
              showNotification(
                withCleanError(
                  "Could not connect to Trust Wallet. Make sure the extension is installed and unlocked",
                  error,
                ),
                "error",
              );
            }
            return;
          }
        } else if (walletType === "okx") {
          // STRICT: Only connect to OKX Wallet extension, NO WalletConnect fallback
          if (!availableWallets?.okxwallet) {
            showNotification(
              "OKX Wallet extension is not installed. Please install the OKX Wallet browser extension.",
              "error",
            );
            isConnectingToWalletRef.current = false;
            return;
          }
          try {
            // Removed "Connecting to..." notification - connection happens quickly
            connectionResult = await connectWithOKXWallet();
            eip1193 =
              connectionResult.provider.provider || connectionResult.provider;
          } catch (error) {
            // If extension connection fails, show error and return - DO NOT fallback to WalletConnect
            console.error("OKX Wallet extension connection error:", error);
            isConnectingToWalletRef.current = false;
            if (error.code === 4001) {
              // User cancelled - don't notify, they already know
            } else {
              showNotification(
                withCleanError(
                  "Could not connect to OKX Wallet. Make sure the extension is installed and unlocked",
                  error,
                ),
                "error",
              );
            }
            return;
          }
        } else {
          // Fall back to WalletConnect (mobile or QR code)
          const WALLETCONNECT_PROJECT_ID =
            process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
          if (!WALLETCONNECT_PROJECT_ID) {
            // If extension is not installed and WalletConnect is not configured, suggest installing the extension
            isConnectingToWalletRef.current = false;
            if (walletType === "trust") {
              showNotification(
                "Trust Wallet extension is not installed. Please install the Trust Wallet browser extension for desktop wallet support.",
                "error",
              );
            } else if (walletType === "okx") {
              showNotification(
                "OKX Wallet extension is not installed. Please install the OKX Wallet browser extension for desktop wallet support.",
                "error",
              );
            } else {
              showNotification(
                "Wallet connection is not available. Please use a desktop wallet extension.",
                "error",
              );
            }
            return;
          }

          try {
            // Removed "Connecting via WalletConnect..." notification - connection happens quickly
            connectionResult = await connectWithWalletConnect(
              WALLETCONNECT_PROJECT_ID,
            );
            eip1193 = connectionResult.walletConnect;
          } catch (error) {
            isConnectingToWalletRef.current = false;
            if (error.code === 4001) {
              // User cancelled - don't notify, they already know
            } else {
              showNotification(
                withCleanError("Could not connect via WalletConnect", error),
                "error",
              );
            }
            return;
          }
        }

        if (
          !connectionResult ||
          !connectionResult.accounts ||
          connectionResult.accounts.length === 0
        ) {
          showNotification("No accounts returned from wallet", "error");
          isConnectingToWalletRef.current = false;
          return;
        }

        const primaryAccount = connectionResult.accounts[0];

        // Wire the provider/signer (from extension or WalletConnect)
        setActiveEip1193(eip1193);
        await setWalletConnectedWithProvider(
          primaryAccount,
          connectionResult.provider, // ethers.Web3Provider
          connectionResult.signer, // ethers.Signer
          origin,
        );

        // Store WalletConnect provider only if we used WalletConnect
        if (connectionResult.walletConnect) {
          setWalletConnectProvider(connectionResult.walletConnect);
        }

        // Clear suppression on successful wallet connection
        suppressAutoConnectRef.current = false;
        // Reset flag after successful connection
        isConnectingToWalletRef.current = false;

        // Register event listeners on the provider
        // Register event listeners
        registerHandlers(
          eip1193,
          {
            accountsChanged: (accs) => {
              if (accs.length === 0) {
                setWalletDisconnected();
                return;
              }
              // Use the same provider when reconnecting
              const currentProvider = provider;
              const currentSigner = signer;
              if (currentProvider && currentSigner) {
                setWalletConnectedWithProvider(
                  accs[0],
                  currentProvider,
                  currentSigner,
                  origin,
                );
              } else {
                setWalletConnected(accs[0], origin);
              }
            },
            chainChanged: (chainId) => {
              const newChainId = parseInt(chainId, 16);
              setCurrentChainId(newChainId);
              setTimeout(checkConfidentialAccount, 1000);
            },
            disconnect: connectionResult.walletConnect
              ? () => {
                  setWalletDisconnected();
                }
              : undefined,
          },
          origin,
        );

        // Post-connection: switch to saved chain preference if needed (non-blocking)
        const preferred = getSavedChainId();
        if (preferred && NETWORK_CONFIGS[preferred]) {
          try {
            const currentNetwork = await connectionResult.provider.getNetwork();
            const preferredConfig = NETWORK_CONFIGS[preferred];
            const preferredChainId = parseInt(preferredConfig.chainId, 16);
            const currentChainIdNum = Number(currentNetwork.chainId);

            if (currentChainIdNum !== preferredChainId) {
              switchToChain(preferred).catch((err) => {
              });
            }
          } catch (err) {
          }
        }

        return;
      } catch (error) {
        console.error(`Error connecting to ${walletId}:`, error);
        // Reset flag on error
        isConnectingToWalletRef.current = false;
        if (error.code === 4001) {
          // User cancelled - don't notify, they already know
        } else if (error.code === -32002) {
          // Request already pending - don't notify, wallet already shows this
        } else {
          showNotification(
            withCleanError("Could not connect via WalletConnect", error),
            "error",
          );
        }
        return;
      } finally {
        updateConnectTarget((prev) => {
          if (origin === "trust") {
            return prev === CONNECT_TARGETS.TRUST ? CONNECT_TARGETS.NONE : prev;
          }
          return prev === CONNECT_TARGETS.OKX ? CONNECT_TARGETS.NONE : prev;
        });
      }
    }

    // Reset flag if we somehow exit without connecting (shouldn't happen, but safety)
    if (isConnectingToWalletRef.current) {
      console.warn(
        "[App.js] Warning: isConnectingToWalletRef still true after handleWalletSelect completed",
      );
      setTimeout(() => {
        isConnectingToWalletRef.current = false;
      }, 2000);
    }

    // Unknown wallet
    isConnectingToWalletRef.current = false;
    showNotification(`Unknown wallet: ${walletId}`, "error");
  };
  handleWalletSelectRef.current = handleWalletSelect;

  // Generic function to switch to any supported chain
  const switchToChain = async (chainId) => {
    const networkConfig = NETWORK_CONFIGS[chainId];
    if (!networkConfig) {
      showNotification(
        `Network configuration not found for ${chainId}`,
        "error",
      );
      return false;
    }

    const targetDec = parseInt(networkConfig.chainId, 16);

    // Only use an already-connected wallet.
    // Do NOT fall back to window.ethereum / MetaMask here,
    // or MetaMask will steal focus during Privy flows.
    const active =
      activeEip1193 || walletConnectProvider;

    // If the wallet is already on the target chain, skip rebuildContract.
    // Rebuilding creates a new `contract` object that used to re-trigger
    // auto account-creation and abort before the wallet popup appeared.
    // Only skip when app state already matches this selection — otherwise a
    // UI chain change with the wallet already on that network would leave
    // `contract` wired to the previous chain.
    const appAlreadyOnTarget =
      currentChainId === chainId ||
      Number(currentChainId) === targetDec ||
      currentChainId === networkConfig.chainId;

    const walletAlreadyOnTarget = async () => {
      // Fail closed: only skip the real switch when we positively read the
      // wallet chain id. Falling back to app state left Privy on Arb One while
      // the UI showed Sepolia, so Secure Claim signed on mainnet.
      if (!active?.request) return false;
      try {
        const hexId = await active.request({ method: "eth_chainId" });
        return parseInt(hexId, 16) === targetDec;
      } catch (_) {
        return false;
      }
    };

    if (appAlreadyOnTarget && (await walletAlreadyOnTarget())) {
      if (active) {
        retargetHybridChain(active, targetDec);
      }
      setSavedChainId(chainId);
      return true;
    }

    const rebuildForSelection = async (eip1193Override = null) => {
      const providerForRebuild = eip1193Override || active;
      if (!providerForRebuild?.request) {
        setSavedChainId(chainId);
        return;
      }
      setSavedChainId(chainId);
      await rebuildContractForActiveChain({
        activeProvider: providerForRebuild,
        updateProvider: setProvider,
        updateSigner: setSigner,
        updateContract: setContract,
        updateChainId: setCurrentChainId,
        preferredChainKey: chainId,
        updateChainPreference: (chainKey) => {
          setSavedChainId(chainKey);
        },
      });
      // Re-read exists/finalized against the newly selected contract so the
      // Transfer CTA doesn't stay stuck on "Creating account..." after a switch.
      try {
        await checkConfidentialAccountRef.current?.();
      } catch (err) {
        console.error("Failed to refresh account status after chain switch:", err);
      }
    };

    // Privy wallets must switch through wallet.switchChain(decimal). Raw
    // wallet_switchEthereumChain on the EIP-1193 provider is unreliable and
    // often leaves the wallet on Sepolia while the UI is on Arbitrum One.
    // This holds for external wallets too — Privy adds the chain to the wallet
    // when it isn't known yet, which the raw call does not.
    if (activeWalletOriginRef.current === CONNECT_TARGETS.PRIVY) {
      const privyWallet = resolvePrivyWallet(
        privyWalletsRef.current,
        privyUserRef.current,
      );
      if (!privyWallet?.switchChain) {
        if (!active?.request) {
          // Not wired yet — remember selection; connect flow will switch.
          setSavedChainId(chainId);
          return true;
        }
      } else {
        try {
          await privyWallet.switchChain(targetDec);
          const rawEip1193 =
            (await privyWallet.getEthereumProvider?.()) || active;
          if (rawEip1193) {
            const eip1193 = makeHybridEip1193(rawEip1193, targetDec);
            setActiveEip1193(eip1193);
            retargetHybridChain(eip1193, targetDec);
            await rebuildForSelection(eip1193);
          } else {
            await rebuildForSelection();
          }
          return true;
        } catch (switchError) {
          const errorMessage = switchError?.message?.toLowerCase?.() || "";
          if (
            switchError?.code === 4001 ||
            errorMessage.includes("rejected") ||
            errorMessage.includes("denied")
          ) {
            return false;
          }
          if (
            errorMessage.includes("unsupported chainid") ||
            errorMessage.includes("unsupported chain")
          ) {
            console.error(
              `[Privy] switchChain(${targetDec}) unsupported:`,
              switchError,
            );
            showNotification(
              `Privy does not support ${networkConfig.chainName} (chainId: ${targetDec}). Check that it is listed in Privy supportedChains.`,
              "error",
            );
            return false;
          }
          console.error(
            `[Privy] Failed to switch to ${networkConfig.chainName}:`,
            switchError,
          );
          showNotification(
            withCleanError(
              `Could not switch Privy wallet to ${networkConfig.chainName}`,
              switchError,
            ),
            "error",
          );
          return false;
        }
      }
    }

    if (!active?.request) {
      // If no wallet is connected, silently allow chain selection to proceed
      // Chain switching will happen once wallet is connected
      return true;
    }

    try {
      await active.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: networkConfig.chainId }],
      });
      retargetHybridChain(active, networkConfig.chainId);
      await rebuildForSelection();
      return true;
    } catch (error) {
      // Handle different error codes and messages
      const errorCode = error.code;
      const errorMessage = error.message?.toLowerCase() || "";

      // Check if it's a "chain not found" or "not connected" error
      // Error code 4902 = chain not added
      // Some wallets return different codes for unsupported chains
      const isChainNotFound =
        errorCode === 4902 ||
        errorCode === -32602 ||
        errorMessage.includes("not connected") ||
        errorMessage.includes("not found") ||
        errorMessage.includes("does not support") ||
        errorMessage.includes("unrecognized chain");

      if (isChainNotFound) {
        // Chain not added or not supported, try to add it
        try {
          await active.request({
            method: "wallet_addEthereumChain",
            params: [networkConfig],
          });
          await rebuildForSelection();

          // After adding, try switching again
          try {
            await active.request({
              method: "wallet_switchEthereumChain",
              params: [{ chainId: networkConfig.chainId }],
            });
            retargetHybridChain(active, networkConfig.chainId);
            await rebuildForSelection();
            return true;
          } catch (switchError) {
            console.error(
              `Failed to switch to ${networkConfig.chainName} after adding:`,
              switchError,
            );
            showNotification(
              `Added ${networkConfig.chainName} but failed to switch. Please switch manually in your wallet.`,
              "warning",
            );
            return false;
          }
        } catch (addError) {
          console.error(
            `Failed to add ${networkConfig.chainName} network:`,
            addError,
          );
          showNotification(
            `Failed to add ${networkConfig.chainName} network. ` +
              `Please add it manually in your wallet settings: Chain ID ${networkConfig.chainId} (${parseInt(networkConfig.chainId, 16)})`,
            "error",
          );
          return false;
        }
      } else if (errorCode === 4001) {
        // User rejected the request - don't notify, user already knows
        return false;
      } else {
        // Other errors - log and show user-friendly message
        console.error(`Error switching to ${networkConfig.chainName}:`, error);
        showNotification(
          withCleanError(
            `Could not switch to ${networkConfig.chainName}. Add the network in your wallet and try again`,
            error,
          ),
          "error",
        );
        return false;
      }
    }
  };

  // Legacy helpers kept for any remaining Sepolia-only call sites.
  const switchToArbitrumSepolia = () => switchToChain("arbitrum");

  const addNetworkForChainKey = async (chainKey) => {
    const networkConfig = NETWORK_CONFIGS[chainKey];
    if (!networkConfig) {
      showNotification(`Network configuration not found for ${chainKey}`, "error");
      return;
    }
    try {
      // Only use an already-connected wallet.
      // Do NOT fall back to window.ethereum / MetaMask here,
      // or MetaMask will steal focus during Privy flows.
      const active =
        activeEip1193 || walletConnectProvider;
      if (!active?.request) {
        showNotification("Connect a wallet before adding networks.", "error");
        return;
      }

      await active.request({
        method: "wallet_addEthereumChain",
        params: [networkConfig],
      });
    } catch (error) {
      console.error(`Failed to add ${networkConfig.chainName} network:`, error);
      showNotification(
        `Failed to add ${networkConfig.chainName} network. Please add it manually in your wallet.`,
        "error",
      );
    }
  };

  const addArbitrumSepoliaNetwork = () => addNetworkForChainKey("arbitrum");

  // formatAddress is already defined at top level, no need for useCallback wrapper

  const handleShowKeysBanner = (keypair) => {
    if (!keypair) return;
    setDerivedKeys({
      private_key: keypair.private_key,
      public_key: keypair.public_key,
    });
    setKeyBannerVisible(true);
  };

  const handleHideKeysBanner = () => {
    setKeyBannerVisible(false);
    // Don't clear derivedKeys - just hide the banner
    // The keys should remain available for the "View Keys" button
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showNotification("Copied to clipboard", "success");
      })
      .catch(() => {
        showNotification("Failed to copy", "error");
      });
  };

  // Compute the 3 visibility flags for right banner stack
  // Hide the banner if there's a pending deposit or any pending action
  const showLetsGetStartedBanner = !!(
    userAccount &&
    currentTokenData &&
    confidentialBalance === 0 &&
    bannerVisible &&
    !hasAccountPendingAction &&
    !isPendingKind(pendingActionKind, PENDING_KIND_DEPOSIT)
  );

  // Check if secure claim is in-flight for the current token (same logic as header button).
  // Include on-chain APPLY pending so UI stays hidden after refresh while the claim is processing.
  const claimKey =
    userAccount && currentTokenData?.chainId && currentTokenData?.address
      ? `${userAccount.toLowerCase()}::${currentTokenData.chainId}::${currentTokenData.address.toLowerCase()}`
      : null;
  const secureClaimInFlight =
    (claimKey ? !!secureClaimInFlightByKey[claimKey] : false) ||
    isPendingKind(pendingActionKind, PENDING_KIND_APPLY);

  const showPendingBanner = !!(
    currentTokenData &&
    pendingBalance != null &&
    !isZeroish(pendingBalance) &&
    !pendingBalanceBannerDismissed &&
    !secureClaimInFlight
  );

  // Show InviteGate if enabled and user doesn't have access
  if (INVITE_GATE_ENABLED && !hasAccess) {
    return <InviteGate onAccessGranted={handleAccessGranted} />;
  }

  return (
    <div className="App st-app">
      {/* FULL-BLEED HEADER — MobileShell (rendered inside TabbedOperations
          below 767px) draws its own header and bottom nav for a 100dvh
          screen, so this desktop chrome is skipped on mobile rather than
          stacking above it and pushing the shell's bottom nav offscreen. */}
      {!isMobileViewport && (
      <header className="site-header">
        <div className="header-content st-header">
          <div className="header-left st-header__left">
            <img
              src="/stabletrustpaylogo.png"
              alt="Logo"
              className="brand-logo st-logo"
              onClick={() => {
                setActiveTab("transfer");
                if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              title="Home"
              style={{ cursor: "pointer" }}
            />
            <div className="header-link-group st-nav">
              <a
                href="/how-it-works.html"
                className="header-link st-nav__link"
                onClick={closeMobileMenu}
              >
                How it works
              </a>
              <a
                href="https://docs.fairblock.network/ct/guides/apps/stabletrust-app"
                target="_blank"
                rel="noopener noreferrer"
                className="header-link st-nav__link"
                onClick={closeMobileMenu}
              >
                Learn
              </a>
            </div>
          </div>
          <button
            type="button"
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="primary-navigation"
          >
            <i className={`fas fa-${isMobileMenuOpen ? "times" : "bars"}`}></i>
          </button>
          <div
            className={`header-actions ${isMobileMenuOpen ? "open" : ""}`}
            id="primary-navigation"
            role="navigation"
            aria-label="Primary"
          >
            {/*
                          Hide the header Secure Claim button while a secure-claim request is in-flight for the
                          currently selected token (session flag or on-chain APPLY), to prevent repeated
                          signatures / duplicate requests — including after a page refresh mid-claim.
                        */}
            {(() => {
              const canShow =
                pendingBalance != null &&
                !isZeroish(pendingBalance) &&
                currentTokenData &&
                !secureClaimInFlight;
              if (!canShow) return null;
              return (
                <button
                  className="secure-claim-button"
                  onClick={() => onSecureClaimClick?.()}
                >
                  {`Secure Claim: ${pendingBalance !== null && pendingBalance !== undefined ? (pendingBalance / 100).toFixed(2) : "0"} ${toConfidentialSymbol(currentTokenData.symbol, currentTokenData.name)}`}
                </button>
              );
            })()}
            <div className="header-link-group mobile-header-links">
              <a
                href="/how-it-works.html"
                className="header-link"
                onClick={closeMobileMenu}
              >
                How it works
              </a>
              <a
                href="https://docs.fairblock.network/ct/guides/apps/stabletrust-app"
                target="_blank"
                rel="noopener noreferrer"
                className="header-link"
                onClick={closeMobileMenu}
              >
                Learn
              </a>
            </div>
            {userAccount && (
              <button
                type="button"
                onClick={() => openRequestModalFn?.()}
                title="Request a Payment"
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: 10,
                  height: "var(--pill-h)",
                  minHeight: "var(--pill-h)",
                  maxHeight: "var(--pill-h)",
                  boxSizing: "border-box",
                  flex: "none",
                  padding: "0 14px",
                  border: "1px solid #16181A",
                  borderRadius: 999,
                  background: "#16181A",
                  color: "#ffffff",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                Request a Payment
              </button>
            )}
            {userAccount && (() => {
              const gasChip = getGasChipVisuals(
                balances.gasState,
                balances.nativeBalance,
                balances.nativeSymbol,
              );
              return (
                <div
                  className="gas-chip"
                  role="button"
                  tabIndex={0}
                  title="Balances"
                  onClick={openBalances}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openBalances();
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: 10,
                    height: "var(--pill-h)",
                    minHeight: "var(--pill-h)",
                    maxHeight: "var(--pill-h)",
                    boxSizing: "border-box",
                    flex: "none",
                    padding: "0 12px",
                    border: `1px solid ${gasChip.border}`,
                    borderRadius: 999,
                    background: gasChip.background,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.04em",
                    color: gasChip.ink,
                    cursor: "pointer",
                  }}
                >
                  <span>{gasChip.label}</span>
                </div>
              );
            })()}
            <div className="header-link-group">
              <WalletConnection
                userAccount={userAccount}
                hasActiveAccount={hasActiveAccount}
                onConnect={openWalletSelectionModal}
                onDisconnect={setWalletDisconnected}
                privyIdentityLabel={
                  activeWalletOrigin === CONNECT_TARGETS.PRIVY
                    ? privyIdentityLabel
                    : null
                }
                onSwitchAccount={authenticated ? switchPrivyAccount : undefined}
                switchingAccount={privySwitching}
                onRefresh={refreshAccountStatus}
                accountData={accountData}
                provider={provider}
                contract={contract}
                currentChainId={currentChainId}
                onShowKeysBanner={handleShowKeysBanner}
                keypair={derivedKeys}
                keyLoading={false}
                keyError={null}
                onSelectWallet={handleWalletSelect}
              />
            </div>
          </div>
        </div>
      </header>
      )}

      {/* PAGE CONTENT */}
      <div className="container">
        {/* Right-side stacked banners */}
        <div className="right-banner-stack" aria-label="Status banners">
          {/* Let's get started (deposit banner) */}
          {showLetsGetStartedBanner && (
            <div className="right-banner-card">
              <button
                className="right-banner-close"
                onClick={() => setBannerVisible(false)}
                aria-label="Close"
              >
                ✕
              </button>

              <div className="right-banner-body">
                <div className="right-banner-title">Go confidential</div>
                <div className="right-banner-text">
                  Deposit stablecoins to make it confidential.
                </div>

                <button
                  className="right-banner-cta"
                  onClick={() => {
                    setActiveTab("deposit");
                    setBannerVisible(false);
                  }}
                >
                  Deposit Now
                </button>
              </div>
            </div>
          )}

          {/* 3) Pending balance */}
          {showPendingBanner && (
            <div className="right-banner-card pending-balance">
              <button
                className="right-banner-close"
                onClick={() => setPendingBalanceBannerDismissed(true)}
                aria-label="Close"
              >
                ✕
              </button>

              <div className="right-banner-body">
                <div className="right-banner-title">
                  Pending balance available
                </div>
                <div className="right-banner-text">
                  You have{" "}
                  {pendingBalance !== null && pendingBalance !== undefined
                    ? (pendingBalance / 100).toFixed(2)
                    : "0"}{" "}
                  C{currentTokenData?.symbol || ""} ready to claim. Secure it to
                  your available balance now.
                </div>

                <button
                  className="right-banner-cta"
                  onClick={() => onSecureClaimClick?.()}
                >
                  Secure Claim
                </button>
              </div>
            </div>
          )}
        </div>

        {keyBannerVisible && derivedKeys && (
          <div
            className="key-banner-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Derived Keys"
          >
            <div className="key-banner-card">
              <div className="key-banner-header">
                <h3 className="key-banner-title">Derived Keys</h3>
                <button
                  className="key-banner-close"
                  aria-label="Close"
                  onClick={handleHideKeysBanner}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="key-banner-body">
                <div className="key-item">
                  <label>Private Key</label>
                  <div className="key-input-container">
                    <input
                      type="text"
                      className="key-input"
                      readOnly
                      value={derivedKeys.private_key}
                    />
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(derivedKeys.private_key)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="key-item">
                  <label>Public Key</label>
                  <div className="key-input-container">
                    <input
                      type="text"
                      className="key-input"
                      readOnly
                      value={derivedKeys.public_key}
                    />
                    <button
                      className="copy-button"
                      onClick={() => copyToClipboard(derivedKeys.public_key)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
                <div className="key-info-text">
                  <small>
                    Keys are deterministically derived from your wallet
                    signature and will be the same each time you connect with
                    this wallet.
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications */}
        <div className="notifications">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification ${notification.type}`}
            >
              {notification.type !== "error" && (
                <i
                  className={`fas fa-${
                    notification.type === "success"
                      ? "check-circle"
                      : "info-circle"
                  }`}
                ></i>
              )}
              <span className="notification-message">
                {notification.message}
              </span>
              {/* Was gated behind notification.persistent — most
                  notifications don't set that flag, so they had no way to
                  be dismissed before their auto-hide timer ran out. Every
                  notification gets a close button now regardless. */}
              <button
                className="notification-close"
                onClick={() =>
                  setNotifications((prev) =>
                    prev.filter((n) => n.id !== notification.id),
                  )
                }
                aria-label="Close notification"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="main-content">
          <div
            className={`main-content-layout ${userAccount && isZeroish(confidentialBalance) && bannerVisible ? "with-banner" : ""}`}
          >
            <div className="main-card">
              <TabbedOperations
                key={userAccount || "disconnected"}
                contract={contract}
                userAccount={userAccount}
                wasmModule={wasmModule}
                provider={provider}
                showNotification={showNotification}
                onOperationComplete={checkConfidentialAccount}
                onKeypairUpdate={setDerivedKeys}
                accountData={accountData}
                hasActiveAccount={hasActiveAccount}
                accountExists={accountExists}
                onConnect={openWalletSelectionModal}
                onDisconnect={setWalletDisconnected}
                incomingRequest={authenticated && requestAccepted ? pendingRequest : null}
                onIncomingConsumed={clearPendingRequest}
                confidentialBalance={confidentialBalance}
                setConfidentialBalance={setConfidentialBalance}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onTokenDataChange={setCurrentTokenData}
                onPendingBalanceChange={setPendingBalance}
                onSecureClaimRegister={setOnSecureClaimClick}
                onSelectTokenRegister={setSelectTokenFn}
                onRequestModalRegister={setOpenRequestModalFn}
                onSecureClaimInFlightChange={handleSecureClaimInFlightChange}
                switchToChain={switchToChain}
                activeWalletOrigin={activeWalletOrigin}
                privyIdentityLabel={authenticated ? privyIdentityLabel : null}
                onSwitchAccount={authenticated ? switchPrivyAccount : undefined}
                switchingAccount={privySwitching}
                gasState={balances.gasState}
                gasRuns={balances.gasRuns}
                nativeSymbol={balances.nativeSymbol}
                nativeBalance={balances.nativeBalance}
                walletBalances={balances.wallet}
                refreshWalletBalances={balances.refresh}
              />
            </div>

            {/* Deposit banner moved to right-banner-stack */}
          </div>
        </div>
      </div>

      {/* Shared Wallet Selection Modal */}
      <WalletSelectionModal
        isOpen={showWalletSelectionModal}
        onClose={() => setShowWalletSelectionModal(false)}
        onSelectWallet={handleWalletSelect}
        availableWallets={availableWalletsForModal}
        privyIdentityLabel={authenticated ? privyIdentityLabel : null}
        onSwitchAccount={authenticated ? switchPrivyAccount : undefined}
      />

      {/* Recipient-facing payment-request popup (shown when opened from a request link) */}
      {pendingRequest && !requestAccepted && (
        <PaymentRequestModal
          request={pendingRequest}
          onPay={acceptPaymentRequest}
          onDecline={declinePaymentRequest}
        />
      )}

      {/* Balances & gas — see handoff/BALANCES.md */}
      {balancesOpen && userAccount && (
        <BalancesModal
          onClose={closeBalances}
          address={userAccount}
          chainKey={currentTokenData?.chainId || null}
          chainName={balances.chainName}
          nativeSymbol={balances.nativeSymbol}
          nativeBalance={balances.nativeBalance}
          gasRuns={balances.gasRuns}
          gasState={balances.gasState}
          wallet={balances.wallet}
          activeTokenSymbol={currentTokenData?.symbol || null}
          confidentialBalance={confidentialBalance}
          onDeposit={(tokenId) => {
            if (tokenId) selectTokenFn?.(tokenId);
            setActiveTab("deposit");
            closeBalances();
          }}
        />
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        showNotification={showNotification}
        userAccount={userAccount}
        chainId={currentChainId}
        walletType={activeWalletOrigin}
      />

      {/* FOOTER — hidden on mobile for the same reason as the header above. */}
      {!isMobileViewport && (
      <footer className="site-footer st-footer">
        <div>
          Secured by{" "}
          <a
            href="https://x.com/0xfairblock?s=20"
            target="_blank"
            rel="noopener noreferrer"
            className="fairblock-link"
          >
            Fairblock
          </a>
        </div>
        <div className="footer-separator">·</div>
        <div>
          <button
            className="footer-link-button"
            onClick={() => setShowFeedbackModal(true)}
            type="button"
          >
            Support / Report a bug
          </button>
        </div>
      </footer>
      )}
    </div>
  );
}

export default App;
